import React, { useEffect, useMemo, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Alert,
  Button,
  Chip,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  IconButton,
  useMediaQuery,
  Tab,
  Tabs,
  Divider,
} from '@mui/material';
import Tooltip from '../LibraryTooltip';
import { useTheme } from '@mui/material/styles';
import {
  PlayArrowOutlined as PlayArrowIcon,
  ContentCopyOutlined as CopyIcon,
  RefreshOutlined as RefreshIcon,
  StopOutlined as StopIcon,
  ExpandMoreOutlined as ExpandMoreIcon,
  StarOutlined as StarIcon,
  North as SendIcon,
  AttachFileOutlined as AttachFileIcon,
  DownloadOutlined as DownloadIcon,
  MoreVertOutlined as MoreVertIcon,
  ChatBubbleOutline as ChatModeIcon,
  CloseOutlined as CloseIcon,
  ArticleOutlined as CreatorModeIcon,
  OpenInNewOutlined as OpenInNewIcon,
} from '@mui/icons-material';
import SafeMarkdown from '../SafeMarkdown';
import { buildCapabilityMcpHandoff } from '../../../utils/capabilityMcpHandoff';
import { assistantChatMarkdownSx } from '../markdownBodySx';
import { assistantPrimaryButtonSx, assistantSecondaryButtonSx, assistantTextButtonSx } from '../../freestyle/assistantButtonStyles';
import { executePrompt as executePromptApi, fetchWebContext, parseDocument } from '../../../services/api';
import { buildDocumentBlockMap, applyBlockReplacements } from '../../../utils/documentBlockMap';
import { buildFullExecutionPayload } from '../../../utils/formatExecutionContext';
import { serializeTemporaryContext, summarizeContextDiagnostics } from '../../../utils/contextDiagnostics';
import {
  getCreatorContinuationDecision,
  getCreatorMaxTokens,
  getPromptCrewExecutionFit,
  isCreatorLongFormPrompt,
  looksLikeRestatedCapabilityResponse,
  parseCreatorDocumentResponse,
  parseCreatorStatus,
  summarizeCreatorDiagnostics,
} from '../../../utils/creatorExecution';
import {
  CREATOR_EDIT_SYSTEM_PROMPT,
  CREATOR_SYSTEM_PROMPT,
  buildRunAnywayCreatorSystemPrompt,
  buildCreatorContinueFullPrompt,
  buildCreatorContinuePrompt,
  buildExecuteChatSystemPrompt,
} from '../../../prompts/systemPrompts';
import { formatUsagePlatformLabel } from '../../../constants/usageTargets';
import CreateFlowSummaryCard from '../../prompt-builder/createFlow/CreateFlowSummaryCard';
import CreateFlowStepCard from '../../prompt-builder/createFlow/CreateFlowStepCard';
import GeneratedCapabilityCard from '../../prompt-builder/createFlow/GeneratedCapabilityCard';
import KnowledgeDiscoveryCard from '../../prompt-builder/createFlow/KnowledgeDiscoveryCard';
import KnowledgeReviewCard from '../../prompt-builder/createFlow/KnowledgeReviewCard';
import KnowledgeSuggestionCard from '../../prompt-builder/createFlow/KnowledgeSuggestionCard';
import {
  CreateFlowFinalizeCompleteCard,
  CreateFlowFinalizeInviteCard,
  CreateFlowFinalizeResultCard,
  CreateFlowMetadataInviteCard,
  CreateFlowVariablesInviteCard,
  CreateFlowVariablesResultCard,
} from '../../prompt-builder/createFlow/CreateFlowFinishCards';
import ContextAddMenu from '../context-display/ContextAddMenu';
import AssistantLanding from '../../freestyle/AssistantLanding';
import DiscoveryResultsList from '../../freestyle/discovery/DiscoveryResultsList';
import useConversationScroll from './useConversationScroll';
import { assistantDocumentSx } from '../../prompt-builder/createFlow/assistantDocumentStyles';
import {
  ASSISTANT_CHAT_SURFACE,
  ASSISTANT_CONVERSATION_COLUMN_SX,
  ASSISTANT_FLAT_ASSISTANT_SURFACE,
  ASSISTANT_THREAD_BG,
  ASSISTANT_USER_CHAT_SURFACE,
  CHAT_COMPOSER_SURFACE,
  USER_CHAT_SURFACE,
  getChatTheme,
} from './chatPanelSurfaces';
import { CHAT_AVAILABLE_MODELS } from './chatAvailableModels';
import { resolveRecommendedModel } from '../../../constants/aiRouting';
import {
  executeChromeIconButtonInGroupSx,
  ExecuteChromeGroup,
  ExecuteChromeGroupDivider,
} from './executeChromeUi';
import { colors, libraryTabSx } from '../../../designTokens';
import { PRODUCT_TERMS } from '../../../constants/productTerminology';
import { resolveCapabilityType } from '../../../constants/builderCategories';

const BUILDER_CARD_ONLY_KINDS = new Set([
  'summary',
  'knowledge_review',
  'generated',
  'refined',
  'finalize_invite',
  'finalize_result',
  'finalize_complete',
  'builder_complete',
]);

const CHAT_MAX_TOKENS = 2400;
const MAX_CHAT_CONTINUE_ROUNDS = 3;
const MAX_CREATOR_CONTINUE_ROUNDS = 5;

function isPromptCrewRunnable(prompt = {}, executeMode = 'chat') {
  return getPromptCrewExecutionFit({ prompt, executeMode }).canRun;
}

function getRunAnywayLabel(executeMode = 'chat') {
  return executeMode === 'creator' ? 'Run anyway in Creator' : 'Run anyway';
}

function buildPromptCrewHandoffActions(executeMode = 'chat') {
  return [
    { action: 'use-in-ai-tool', label: 'Use in your AI tool', variant: 'contained' },
    { action: 'copy-workspace', label: 'Copy workspace', variant: 'outlined' },
    { action: 'download-workspace', label: 'Download workspace', variant: 'outlined' },
    { action: 'run-anyways', label: getRunAnywayLabel(executeMode), variant: 'text' },
  ];
}

function getWorkflowActionIcon(actionName) {
  if (actionName === 'connect-mcp' || actionName === 'use-in-ai-tool') return <OpenInNewIcon fontSize="small" />;
  if (actionName === 'copy-workspace') return <CopyIcon fontSize="small" />;
  if (actionName === 'download-workspace') return <DownloadIcon fontSize="small" />;
  if (actionName === 'run-anyways') return <PlayArrowIcon fontSize="small" />;
  return null;
}

function buildPromptCrewHandoffMessage({ prompt = {}, platformLabel = '', executeMode = 'chat' } = {}) {
  const capabilityType = String(prompt?.capabilityType || 'prompt').toLowerCase();
  const readableType = `${resolveCapabilityType(capabilityType).label.toLowerCase()} capability`;
  const destination = platformLabel && platformLabel !== 'PromptCrew'
    ? ` in ${platformLabel}`
    : '';
  const runtime = capabilityType === 'agent'
    ? 'a connected AI tool that can run on a schedule'
    : 'a connected AI tool';
  const surface = executeMode === 'creator' ? 'Creator' : 'Chat';
  const fallback = executeMode === 'creator' ? 'static artifact' : 'chat';

  return [
    `This ${readableType} needs ${runtime}.`,
    '',
    `Connect PromptCrew through MCP to use it${destination} with the tools, integrations, files, or schedules it needs. You can also copy or download the workspace.`,
    '',
    `Run a best-effort ${fallback} version in PromptCrew ${surface} only when you do not need external actions.`,
  ].join('\n');
}

function buildRunAnywaySystemPrompt({ prompt = {}, platformLabel = '' } = {}) {
  return [
    buildExecuteChatSystemPrompt({
      title: prompt?.title || prompt?.name || '',
      description: prompt?.description || '',
      capabilityType: prompt?.capabilityType || '',
      howToUse: prompt?.howToUse || '',
      whereToUse: prompt?.whereToUse || '',
      usagePlatform: prompt?.usagePlatform || '',
      usagePlatformOther: prompt?.usagePlatformOther || '',
      platformLabel,
    }),
    '',
    'The capability may be designed for an external tool, integration runtime, MCP-enabled autonomous platform, repo, API, or integration. The user clicked "Run anyway" in PromptCrew Chat.',
    'Run the intent of the capability inside this chat to the best of your ability now. The user has already accepted the limitation, so do not return another "PromptCrew is not the right fit" handoff message.',
    'Do not claim you can access external systems, launch agents, edit repos, call APIs, or use MCP unless the needed information is present in the chat or attached knowledge.',
    'If external execution is required, simulate the useful chat-safe part: produce the draft, checklist, plan, spec, commands, implementation guidance, or concrete next artifact directly. Only mention external limits briefly after giving useful output.',
  ].join('\n');
}

function buildRunAnywayCreatorPrompt({ prompt = {}, platformLabel = '' } = {}) {
  return buildRunAnywayCreatorSystemPrompt({
    title: prompt?.title || prompt?.name || '',
    description: prompt?.description || '',
    capabilityType: prompt?.capabilityType || '',
    howToUse: prompt?.howToUse || '',
    whereToUse: prompt?.whereToUse || '',
    usagePlatform: prompt?.usagePlatform || '',
    usagePlatformOther: prompt?.usagePlatformOther || '',
    platformLabel,
  });
}

function safeDownloadName(value, fallback = 'promptcrew-workspace') {
  const name = String(value || fallback)
    .trim()
    .replace(/[^a-z0-9-_]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return name || fallback;
}

function sanitizeWorkspacePathSegment(value, fallback = 'item') {
  const text = String(value || fallback)
    .trim()
    .split('')
    .map((char) => (char.charCodeAt(0) < 32 || '<>:"/\\|?*'.includes(char) ? '-' : char))
    .join('')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return text || fallback;
}

/** Centered reading/writing column — execute + freestyle full-width chat. */
const CONVERSATION_COLUMN_SX = {
  width: '100%',
  maxWidth: 1331,
  mx: 'auto',
};

const USER_MESSAGE_MAX_WIDTH = '550px';
const ASSISTANT_MESSAGE_MAX_WIDTH = '650px';
const ASSISTANT_COMPOSER_MAX_WIDTH = 920;

const DEFAULT_CHAT_EMPTY_TITLE = 'Start a conversation with this capability';
const DEFAULT_CHAT_EMPTY_DESCRIPTION =
  'Ask a question, provide knowledge, or test the capability\'s behavior.';

/** Compact Chat / Creator tabs — matches Library/Builder underline style. */
const executeModeTabSx = {
  ...libraryTabSx,
  minHeight: 36,
  minWidth: 0,
  px: 1.25,
  py: 0.5,
  fontSize: '0.8125rem',
};

/** Compact tabs inside chrome white pill — matches 34px model selector height. */
const executeModeTabOnChromeSx = {
  ...libraryTabSx,
  minHeight: 32,
  height: 32,
  minWidth: 0,
  px: 1.1,
  py: 0,
  fontSize: '0.8125rem',
  lineHeight: 1.25,
};

/** Inline model picker inside a chrome group — no outer pill. */
const executeModelSelectorInGroupSx = {
  minHeight: 34,
  py: 0.35,
  px: 1.1,
  fontSize: '0.8125rem',
  fontWeight: 500,
  textTransform: 'none',
  color: colors.textSecondary,
  border: 'none',
  borderRadius: 0,
  bgcolor: 'transparent',
  boxShadow: 'none',
  cursor: 'pointer',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flexShrink: 1,
  '&:hover': {
    bgcolor: 'rgba(15, 23, 42, 0.06)',
    color: colors.textPrimary,
  },
  '& .MuiButton-endIcon': {
    ml: 0.25,
    color: 'inherit',
    flexShrink: 0,
  },
};

/** Inline model picker — matches header tab typography, no outlined box. */
const executeModelSelectorSx = {
  minHeight: 36,
  py: 0.5,
  px: 1.35,
  fontSize: '0.8125rem',
  fontWeight: 500,
  textTransform: 'none',
  color: colors.textSecondary,
  border: 'none',
  bgcolor: 'transparent',
  boxShadow: 'none',
  cursor: 'pointer',
  '&:hover': {
    bgcolor: 'rgba(15, 23, 42, 0.035)',
    color: colors.textPrimary,
  },
  '& .MuiButton-endIcon': {
    ml: 0.25,
    color: 'inherit',
  },
};

const EXECUTE_SURFACE_DIVIDER = 'rgba(148, 163, 184, 0.1)';
const RAW_TEMP_CONTEXT_EXTENSIONS = new Set([
  'txt', 'md', 'markdown', 'csv', 'json', 'jsonl', 'yaml', 'yml', 'js', 'jsx', 'ts', 'tsx',
  'py', 'sh', 'bash', 'zsh', 'toml', 'xml', 'html', 'htm', 'css', 'scss', 'sass', 'env',
  'ini', 'conf', 'config', 'mjs', 'cjs', 'java', 'go', 'rs', 'rb', 'php', 'cs', 'cpp',
  'c', 'h', 'hpp', 'sql', 'graphql', 'gql', 'dockerfile', 'gitignore', 'dockerignore'
]);
const URL_REGEX = /\bhttps?:\/\/[^\s<>"')]+/gi;

const tempContextId = () => `temp-${crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
const getFileExtension = (fileName = '') => String(fileName).split('.').pop()?.toLowerCase() || '';
const stripFileExtension = (fileName = '') => String(fileName).replace(/\.[^.]+$/, '') || String(fileName || PRODUCT_TERMS.temporaryKnowledge);
const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

/** Extract ---QUESTION--- block; returns { question } or null if not a question response. */
function parseCreatorQuestion(contentString) {
  if (!contentString || typeof contentString !== 'string') return null;
  const qMarker = '---QUESTION---';
  const endMarker = '---END---';
  const qIdx = contentString.indexOf(qMarker);
  if (qIdx === -1) return null;
  const afterQ = contentString.slice(qIdx + qMarker.length).trimStart();
  const endIdx = afterQ.indexOf(endMarker);
  const question = (endIdx >= 0 ? afterQ.slice(0, endIdx) : afterQ).trim();
  return question ? { question } : null;
}

/** Heuristic: content looks like the model asking the user for clarification (no ---DOCUMENT---). */
function isModelAskingQuestion(contentString) {
  if (!contentString || typeof contentString !== 'string') return false;
  const trimmed = contentString.trim();
  if (trimmed.includes('---DOCUMENT---')) return false; // Has document block, not just a question
  const qParsed = parseCreatorQuestion(trimmed);
  if (qParsed) return true;
  const lower = trimmed.toLowerCase();
  const questionPhrases = [
    'could you clarify', 'can you clarify', 'would you clarify',
    'what do you mean', 'which ', 'please provide', 'please specify',
    'can you tell me', 'could you tell me', 'what is the', 'which of the',
    'need more information', 'need more details', 'need to know',
    'before i can', 'to proceed', 'to continue'
  ];
  if (questionPhrases.some((p) => lower.includes(p))) return true;
  if (trimmed.endsWith('?') && trimmed.length < 600) return true; // Short and ends with ?
  return false;
}

const REPLACE_REGEX = /---REPLACE\s+(section_\d+)\s*---\s*([\s\S]*?)---END---/gi;
const APPEND_REGEX = /---APPEND---\s*([\s\S]*?)---END---/i;
const FULL_REGEX = /---FULL---\s*([\s\S]*?)---END---/i;
const EXPLANATION_MARKER = '---EXPLANATION---';

/**
 * Parse creator edit response: ---REPLACE section_N --- ... ---END---, ---APPEND---, ---FULL---, ---EXPLANATION---.
 * Returns { replacements: [{ blockId, content }], appendContent?, fullDocument?, explanation }.
 * FULL block: prefer ---END---; if missing, take content after ---FULL--- until a line that is exactly ---EXPLANATION---
 * (so we don't truncate when the document content itself contains that string).
 */
function parseCreatorEditResponse(contentString) {
  if (!contentString || typeof contentString !== 'string') return null;
  const replacements = [];
  let match;
  REPLACE_REGEX.lastIndex = 0;
  while ((match = REPLACE_REGEX.exec(contentString)) !== null) {
    replacements.push({ blockId: match[1], content: (match[2] || '').trim() });
  }
  const appendMatch = contentString.match(APPEND_REGEX);
  const appendContent = appendMatch ? (appendMatch[1] || '').trim() : null;
  let fullDocument = null;
  const fullMatch = contentString.match(FULL_REGEX);
  if (fullMatch) {
    fullDocument = (fullMatch[1] || '').trim();
  } else {
    const fullIdx = contentString.search(/---FULL---/i);
    if (fullIdx >= 0) {
      const afterFull = contentString.slice(fullIdx + 9).replace(/^\s+/, '');
      if (afterFull.startsWith('---EXPLANATION---')) {
        fullDocument = '';
      } else {
        const lineDelimiter = afterFull.includes('\r\n') ? '\r\n' : '\n';
        const lineStartExp = `${lineDelimiter}---EXPLANATION---`;
        const expIdx = afterFull.indexOf(lineStartExp);
        fullDocument = (expIdx >= 0 ? afterFull.slice(0, expIdx) : afterFull).trim();
      }
    }
  }
  const expIdx = contentString.indexOf(EXPLANATION_MARKER);
  const explanation = expIdx >= 0 ? contentString.slice(expIdx + EXPLANATION_MARKER.length).trim() : '';
  return {
    replacements: replacements.length ? replacements : null,
    appendContent: appendContent || null,
    fullDocument: fullDocument || null,
    explanation,
  };
}

/**
 * Merge a returned ---DOCUMENT--- block into the current full markdown.
 * - If the block starts with a heading (# or ## etc.), find that section in currentDoc and replace it.
 * - Otherwise append the block to the end.
 */
function mergeCreatorSection(currentDoc, returnedBlock) {
  const block = (returnedBlock || '').trim();
  if (!block) return currentDoc || '';
  const doc = (currentDoc || '').trim();
  const firstLine = block.split(/\n/)[0] || '';
  const headingMatch = firstLine.match(/^(#{1,6})\s+(.+)$/);
  if (headingMatch) {
    const level = headingMatch[1].length;
    const title = headingMatch[2].trim().toLowerCase();
    const lines = doc.split(/\n/);
    let startIdx = -1;
    let endIdx = lines.length;
    for (let i = 0; i < lines.length; i++) {
      const m = lines[i].match(/^(#{1,6})\s+(.+)$/);
      if (m) {
        const l = m[1].length;
        const t = m[2].trim().toLowerCase();
        if (startIdx === -1 && l <= level && t === title) {
          startIdx = i;
        } else if (startIdx >= 0 && l <= level) {
          endIdx = i;
          break;
        }
      }
    }
    if (startIdx >= 0) {
      const before = lines.slice(0, startIdx).join('\n').trimEnd();
      const after = lines.slice(endIdx).join('\n').trimStart();
      const joined = [before, block, after].filter(Boolean).join('\n\n');
      return joined;
    }
  }
  return doc ? (doc + '\n\n' + block).trim() : block;
}

function isGenericExplanation(text) {
  if (!text || typeof text !== 'string') return true;
  const t = text.trim().toLowerCase();
  return t.length < 10 || /^(document\s+updated|done|ok|updated\.?)$/.test(t) || t === 'n/a';
}

function creatorChatSummary(parsed, fullContent) {
  const exp = parsed?.explanation?.trim();
  if (exp && !isGenericExplanation(exp)) return exp;
  const doc = parsed?.document || fullContent || '';
  const firstLine = doc.split(/\n/).find((l) => l.trim().length > 0) || '';
  const snippet = firstLine.trim().slice(0, 120);
  if (snippet) return `Document updated. Preview: ${snippet}${firstLine.length > 120 ? '…' : ''}`;
  return 'Document updated. (No summary provided.)';
}

function buildChatContinuePrompt() {
  return [
    'Continue the previous answer from exactly where it stopped.',
    'Do not restart or summarize. Continue with the next sentence or bullet only.',
  ].join('\n');
}

export function ExecuteHeaderBar({
  executeMode,
  onExecuteModeChange,
  selectedModel,
  onModelChange,
  modelMenuAnchor,
  onModelMenuAnchorChange,
  prompt,
  onExportDocx,
  canExportDocx = false,
  showModeToggle = true,
  modeChangeDisabled = false,
  contextAction = null,
  leadingActions = null,
  navActions = null,
  trailingActions = null,
  layout = 'default',
  chromePlacement = 'full',
  compactChrome = false,
  showCloseButton = false,
  onClose,
}) {
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [modeMenuAnchor, setModeMenuAnchor] = useState(null);
  const isExecuteLayout = layout === 'execute';
  const isAssistantLayout = layout === 'assistant';
  const isChromeLayout = isExecuteLayout || isAssistantLayout;
  /** Creator mode or mobile chat: icon-only; desktop chat mode: labeled tabs. */
  const modeToggleIconOnly = compactChrome;
  const modelsForDropdown = useMemo(
    () => CHAT_AVAILABLE_MODELS.filter((m) => m.type === 'chat'),
    []
  );
  const recommendedModelId = prompt?.model || null;
  const sortedModels = useMemo(() => {
    const models = [...modelsForDropdown];
    if (recommendedModelId && models.some((m) => m.id === recommendedModelId)) {
      models.sort((a, b) =>
        a.id === recommendedModelId ? -1 : b.id === recommendedModelId ? 1 : 0
      );
    }
    return models;
  }, [modelsForDropdown, recommendedModelId]);

  const exportControl = typeof onExportDocx === 'function' ? (
    <>
      <Tooltip title="Export document">
        <span>
          <IconButton
            size="small"
            disabled={!canExportDocx}
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            sx={executeChromeIconButtonInGroupSx()}
          >
            <DownloadIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 1.5,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          },
        }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Export
          </Typography>
        </Box>
        <MenuItem
          onClick={() => {
            setExportMenuAnchor(null);
            onExportDocx?.();
          }}
          sx={{ py: 1.5, px: 2, gap: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <DownloadIcon fontSize="small" sx={{ color: 'primary.main' }} />
          </ListItemIcon>
          <ListItemText
            primary="Export as DOCX"
            secondary="Word document"
            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
            secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
          />
        </MenuItem>
      </Menu>
    </>
  ) : null;

  const chromeModeTabsSx = {
    minHeight: 34,
    height: 34,
    px: 0.35,
    '& .MuiTabs-flexContainer': {
      height: 34,
      alignItems: 'center',
    },
    '& .MuiTabs-indicator': {
      height: 2,
      bottom: 5,
      borderRadius: 1,
      backgroundColor: 'primary.main',
    },
  };

  const modeToggleControl = showModeToggle && typeof onExecuteModeChange === 'function' ? isAssistantLayout ? (
    <>
      <Tooltip title="Response mode">
        <Button disabled={modeChangeDisabled} size="small" aria-label={`Response mode: ${executeMode === 'creator' ? 'Creator' : 'Chat'}`} aria-haspopup="menu" aria-expanded={Boolean(modeMenuAnchor)} onClick={(event) => setModeMenuAnchor(event.currentTarget)} startIcon={executeMode === 'creator' ? <CreatorModeIcon sx={{ fontSize: 16 }} /> : <ChatModeIcon sx={{ fontSize: 16 }} />} endIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />} sx={{ color: 'text.secondary', fontSize: '0.8125rem', textTransform: 'none', minHeight: 32 }}>
          {executeMode === 'creator' ? 'Creator' : 'Chat'}
        </Button>
      </Tooltip>
      <Menu anchorEl={modeMenuAnchor} open={Boolean(modeMenuAnchor)} onClose={() => setModeMenuAnchor(null)} PaperProps={{ sx: { borderRadius: '8px', minWidth: 180 } }}>
        {[['chat', 'Chat', ChatModeIcon], ['creator', 'Creator', CreatorModeIcon]].map(([mode, label, Icon]) => <MenuItem key={mode} disabled={modeChangeDisabled} selected={executeMode === mode} onClick={() => { if (!modeChangeDisabled) onExecuteModeChange(mode); setModeMenuAnchor(null); }}><ListItemIcon><Icon fontSize="small" /></ListItemIcon><ListItemText>{label}</ListItemText></MenuItem>)}
      </Menu>
    </>
  ) : (
    <Tabs
      value={executeMode}
      onChange={(_, v) => v && onExecuteModeChange(v)}
      sx={chromeModeTabsSx}
    >
      <Tab
        value="chat"
        icon={<ChatModeIcon sx={{ fontSize: 16 }} />}
        iconPosition={modeToggleIconOnly ? undefined : 'start'}
        label={modeToggleIconOnly ? undefined : 'Chat'}
        aria-label="Chat"
        sx={{
          ...executeModeTabOnChromeSx,
          ...(modeToggleIconOnly && {
            minWidth: 34,
            maxWidth: 38,
            px: 0.35,
            '& .MuiTab-icon': { mb: 0, mr: 0 },
          }),
        }}
      />
      <Tab
        value="creator"
        icon={<CreatorModeIcon sx={{ fontSize: 16 }} />}
        iconPosition={modeToggleIconOnly ? undefined : 'start'}
        label={modeToggleIconOnly ? undefined : 'Creator'}
        aria-label="Creator"
        sx={{
          ...executeModeTabOnChromeSx,
          ...(modeToggleIconOnly && {
            minWidth: 34,
            maxWidth: 38,
            px: 0.35,
            '& .MuiTab-icon': { mb: 0, mr: 0 },
          }),
        }}
      />
    </Tabs>
  ) : null;

  const modeToggleControlDefault = showModeToggle && typeof onExecuteModeChange === 'function' ? (
    <Tabs
      value={executeMode}
      onChange={(_, v) => v && onExecuteModeChange(v)}
      sx={{
        minHeight: 36,
        '& .MuiTabs-indicator': {
          height: 2,
          backgroundColor: 'primary.main',
        },
      }}
    >
      <Tab value="chat" label="Chat" sx={executeModeTabSx} />
      <Tab value="creator" label="Creator" sx={executeModeTabSx} />
    </Tabs>
  ) : null;

  const modelSelectorControl = (
    <>
      <Button
        variant="text"
        size="small"
        onClick={(e) => onModelMenuAnchorChange(e.currentTarget)}
        endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
        sx={{
          ...(isChromeLayout ? executeModelSelectorInGroupSx : executeModelSelectorSx),
          ...(compactChrome && isChromeLayout && { maxWidth: 116 }),
        }}
      >
        {modelsForDropdown.find((m) => m.id === (selectedModel || 'gpt-6-sol'))?.name || selectedModel || 'gpt-6-sol'}
      </Button>
      <Menu
        anchorEl={modelMenuAnchor}
        open={Boolean(modelMenuAnchor)}
        onClose={() => onModelMenuAnchorChange(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 180,
              mt: 0.5,
              maxHeight: 320,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            },
          },
        }}
        MenuListProps={{ dense: true, sx: { py: 0.5 } }}
      >
        {sortedModels.map((model) => {
          const isRecommended = model.id === recommendedModelId;
          return (
            <MenuItem
              key={model.id}
              selected={model.id === (selectedModel || 'gpt-6-sol')}
              onClick={() => {
                onModelChange?.(model.id);
                onModelMenuAnchorChange(null);
              }}
              sx={{ py: 1, px: 2 }}
            >
              <ListItemText
                primary={model.name}
                secondary={model.provider}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
              />
              {isRecommended && (
                <Chip
                  icon={<StarIcon sx={{ fontSize: 14 }} />}
                  label="Recommended"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    ml: 1,
                    height: 20,
                    fontSize: '0.65rem',
                    '& .MuiChip-label': { pl: 0.25, pr: 0.5 },
                  }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );

  const closeControl = showCloseButton && typeof onClose === 'function' ? (
    <Tooltip title="Close">
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close"
        sx={isChromeLayout ? executeChromeIconButtonInGroupSx() : {
          width: 34,
          height: 34,
          color: colors.textSecondary,
          '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.035)', color: colors.textPrimary },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  ) : null;

  const barSx = {
    px: compactChrome ? 1 : 2.25,
    py: compactChrome ? 0.5 : 0.875,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: compactChrome ? 1 : 2,
    flexWrap: 'wrap',
    flexShrink: 0,
    bgcolor: isChromeLayout
      ? ASSISTANT_THREAD_BG
      : (compactChrome ? 'transparent' : 'background.paper'),
    boxShadow: isChromeLayout || compactChrome ? 'none' : '0 1px 0 rgba(15, 23, 42, 0.06)',
  };

  if (isChromeLayout) {
    const showLeftChrome = contextAction || (isAssistantLayout && trailingActions);
    const showExport = isExecuteLayout && typeof onExportDocx === 'function';
    const showMode = Boolean(modeToggleControl);
    const showNav = isExecuteLayout && navActions;
    const showClose = showCloseButton && typeof onClose === 'function';

    const plainChromeGroupSx = isChromeLayout
      ? {
        bgcolor: 'transparent',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        borderColor: 'transparent',
        boxShadow: 'none',
      }
      : undefined;

    return (
      <Box sx={{ ...barSx, justifyContent: 'space-between', ...(isAssistantLayout && executeMode === 'creator' ? { flexDirection: 'column', alignItems: 'stretch', gap: 0.25 } : {}) }}>
        {showLeftChrome ? (
          <ExecuteChromeGroup sx={{ ...plainChromeGroupSx, minWidth: 0, maxWidth: '100%' }}>
            {contextAction}
            {contextAction && isAssistantLayout && trailingActions ? <ExecuteChromeGroupDivider /> : null}
            {isAssistantLayout && trailingActions}
          </ExecuteChromeGroup>
        ) : null}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <ExecuteChromeGroup sx={{ minWidth: 0, maxWidth: '100%', ...plainChromeGroupSx }}>
            {modelSelectorControl}
            {showMode ? <ExecuteChromeGroupDivider /> : null}
            {modeToggleControl}
            {showExport ? <ExecuteChromeGroupDivider /> : null}
            {showExport && exportControl}
            {showNav ? <ExecuteChromeGroupDivider /> : null}
            {showNav && navActions}
            {showClose ? <ExecuteChromeGroupDivider /> : null}
            {showClose && closeControl}
          </ExecuteChromeGroup>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={barSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto' }}>
        {leadingActions}
        {contextAction}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: compactChrome ? 0.75 : 1.75, flexShrink: 0 }}>
        {exportControl}
        {modeToggleControlDefault}
        {closeControl}
        {modelSelectorControl}
      </Box>
    </Box>
  );
}

/**
 * Embedded chat panel used inside PromptDialog when "Execute" is pressed.
 * This is intentionally in-memory only; closing the modal resets state.
 *
 * Note: This calls the backend `POST /ai/execute-prompt` endpoint.
 * Exposes execute() via ref for external trigger (e.g. Execute button in Prompts section).
 */
const PromptExecuteChatPanel = forwardRef(({
  prompt,
  constructedPrompt,
  includedContexts,
  allContexts, // Optional: same list as parent uses for fullPromptText (linked + temporary). When set, used for getSelectedContexts so contextIds match payload.
  contextCategories = {}, // Prompt-specific folder per context id (same as PromptDialog / saved prompt)
  editedPromptText,
  originalPromptText,
  isPromptLocked,
  fullPromptText, // The full prompt text with contexts (from PromptDialog) - sent to API
  promptOnlyText, // Prompt only (no context) - shown in chat UI for first user message
  autoExecute = true,
  onLockPrompt,
  onExecutingChange, // (isExecuting: boolean) => void - report executing state to parent
  runExecuteOnOpen = false, // when true, run execute() once (e.g. user just opened Execute panel from modal)
  onExecuteTriggered, // () => void - parent sets runExecuteOnOpen false after we've kicked off execute
  executeMode = 'chat', // 'chat' | 'creator'
  onExecuteModeChange, // (mode: 'chat' | 'creator') => void
  variant = 'full', // 'full' | 'shelf' - shelf = narrow right panel in Creator mode
  creatorDocument, // current document content in Creator mode (for vibe edits)
  onCreatorDocumentGenerated, // (documentMarkdown: string, explanation: string) => void - when AI returns document + explanation
  showHeader = true, // when false, header is rendered by parent (shared top bar)
  selectedModel: selectedModelProp, // optional controlled model
  onModelChange, // optional controlled model change
  onClearDocument, // optional; when provided and creatorDocument, show Clear document at top (Creator mode shelf)
  onRestartSession, // optional; when provided, show refresh icon next to add-context and call on click (parent may clear document)
  onAddTemporaryContext, // optional; when provided, "+" button can open temporary context menu
  onAttachExistingContext, // optional; same as above for attaching
  onCreateTemporaryContexts, // optional; adds session-only contexts created from dropped files or pasted URLs
  onTemporaryContextsCreated, // optional; notify parent for success toast after drag/drop/url temp context creation
  emptyStateTitle = null,
  emptyStateDescription = null,
  suppressIntro = false, // when true, skip the pre-execution "Ready to run" screen while auto-executing
  executionSessionState = null, // optional cached execute panel state from parent
  onExecutionSessionStateChange, // optional: report execute panel state for parent caching
  preserveConversationOnExecute = false,
  wrapExecuteAction = null, // (doExecute: () => void) => void — defer first run (e.g. variable gate)
  executionPayloadOverrideRef = null, // ref to one-shot { fullPayload, promptOnlySubstituted } for execute()
  onSaveUserMessage = null, // optional: (message, { includeContext }) => void
  onBeforeExecuteMessage = null, // optional: async ({ model, message, userMessage }) => ({ handled, messages })
  onDiscoveryAction: dispatchDiscoveryAction = null, // optional: ({ action, item, result }) => void
  workspacePromptIds = [],
  builderCapabilityAdded = false,
  onBuilderAction: dispatchBuilderAction = null, // optional: ({ action, payload, message }) => void
  builderSessionActive = false,
  onBuilderComposerSubmit = null,
  builderComposerLocked = false,
  builderStatusLabel = '',
  starterSuggestions = [],
  showRunDiagnostics = false,
  chatUiVariant = 'default', // 'default' | 'assistant'
  assistantLandingEnabled = false,
  suppressAssistantLanding = false,
  onAssistantLandingDismiss = null,
  onAssistantBuildIntent = null,
  onAssistantRefineIntent = null,
  assistantRefineOptions = null,
  assistantLandingRefining = false,
  assistantContext = 'standard',
  getRefineSourceText = null,
  refineApplyLabel = 'Create capability',
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAssistantChatUi = chatUiVariant === 'assistant';
  const chatTheme = getChatTheme(chatUiVariant);
  const activeUserSurface = chatTheme.userSurface;
  const activeAssistantSurface = chatTheme.assistantSurface;
  const activeComposerSurface = chatTheme.composerSurface || CHAT_COMPOSER_SURFACE;
  const [selectedModelInternal, setSelectedModelInternal] = useState(null);
  const recommendedModelId = resolveRecommendedModel({
    ...(prompt || {}),
    allowModelOverride: Boolean(prompt?.model),
  }) || 'gpt-6-sol';
  const selectedModel = selectedModelProp ?? selectedModelInternal ?? recommendedModelId;
  const [testChats, setTestChats] = useState({});
  const setSelectedModel = useCallback((v) => {
    if (!v || v === selectedModel) return;

    // Conversations are keyed by model so testers can compare runs. Seed an
    // unused model with the active conversation instead of dropping them back
    // into the empty Assistant landing screen.
    setTestChats((previous) => {
      const currentMessages = previous[selectedModel] || [];
      const nextMessages = previous[v] || [];
      if (currentMessages.length === 0 || nextMessages.length > 0) return previous;
      return { ...previous, [v]: currentMessages };
    });
    if (onModelChange) onModelChange(v);
    else setSelectedModelInternal(v);
  }, [onModelChange, selectedModel]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isSending, setIsSending] = useState(false); // Track if we're waiting for AI response after sending a message
  const [sendingStatusLabel, setSendingStatusLabel] = useState('AI is thinking...');
  const isBuilderBusy = Boolean(String(builderStatusLabel || '').trim());
  const isChatBusy = isSending || isBuilderBusy;
  const [copySuccess, setCopySuccess] = useState({});
  const [workspaceActionStatus, setWorkspaceActionStatus] = useState({ open: false, message: '', severity: 'success' });
  const [modelMenuAnchor, setModelMenuAnchor] = useState(null);
  const [composerContextMenuAnchor, setComposerContextMenuAnchor] = useState(null);
  const [userMessageSaveAnchor, setUserMessageSaveAnchor] = useState(null);
  const [userMessageSaveTarget, setUserMessageSaveTarget] = useState(null);
  const [creatorUpdatesDone, setCreatorUpdatesDone] = useState(false);
  const [isPreparingContext, setIsPreparingContext] = useState(false);
  const [contextPreparationLabel, setContextPreparationLabel] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const autoExecutedRef = useRef(false);
  const [introSuppressedOnce, setIntroSuppressedOnce] = useState(false);
  const chatsRef = useRef({});
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const handleSendMessageRef = useRef(null);
  const assistantLandingRef = useRef(null);
  const abortRequestedRef = useRef(false);
  const isHydratingSessionRef = useRef(false);
  const skipNextSessionReportRef = useRef(false);
  /** Same object reference we last pushed to parent — avoids hydrate/sync ping-pong. */
  const lastOutgoingSessionRef = useRef(null);
  const lastOutgoingSessionKeyRef = useRef('');

  const getExecutionSessionKey = useCallback((state = {}) => JSON.stringify({
    selectedModel: state.selectedModel || '',
    testChats: state.testChats || {},
    copySuccess: state.copySuccess || {},
    creatorUpdatesDone: Boolean(state.creatorUpdatesDone),
    introSuppressedOnce: Boolean(state.introSuppressedOnce),
    composerInput: state.composerInput || '',
  }), []);

  useEffect(() => {
    chatsRef.current = testChats;
  }, [testChats]);

  useEffect(() => {
    if (!executionSessionState) return;
    // Parent echoes back the same object we just reported; do not re-apply (prevents loops / mid-flight resets).
    if (executionSessionState === lastOutgoingSessionRef.current) return;

    isHydratingSessionRef.current = true;
    skipNextSessionReportRef.current = true;
    lastOutgoingSessionRef.current = executionSessionState;
    lastOutgoingSessionKeyRef.current = getExecutionSessionKey(executionSessionState);

    if (executionSessionState.selectedModel) {
      setSelectedModel(executionSessionState.selectedModel);
    }
    setTestChats(executionSessionState.testChats || {});
    setCopySuccess(executionSessionState.copySuccess || {});
    setCreatorUpdatesDone(Boolean(executionSessionState.creatorUpdatesDone));
    setIntroSuppressedOnce(Boolean(executionSessionState.introSuppressedOnce));
    // Never clear isExecuting / isSending here — that was interrupting async handleExecute and could retrigger auto-run.

    const cachedComposerValue = executionSessionState.composerInput;
    if (messageInputRef.current && typeof cachedComposerValue === 'string') {
      messageInputRef.current.value = cachedComposerValue;
    }

    isHydratingSessionRef.current = false;
  }, [executionSessionState, setSelectedModel, getExecutionSessionKey]);

  useEffect(() => {
    if (!onExecutionSessionStateChange || isHydratingSessionRef.current) return;
    if (skipNextSessionReportRef.current) {
      skipNextSessionReportRef.current = false;
      return;
    }
    // While a request is in flight, avoid pushing every chat delta to parent (reduces rerenders and feedback loops).
    if (isExecuting || isSending) return;
    const payload = {
      selectedModel,
      testChats,
      copySuccess,
      creatorUpdatesDone,
      introSuppressedOnce,
      composerInput: messageInputRef.current?.value || '',
    };
    const payloadKey = getExecutionSessionKey(payload);
    if (payloadKey === lastOutgoingSessionKeyRef.current) return;
    lastOutgoingSessionKeyRef.current = payloadKey;
    lastOutgoingSessionRef.current = payload;
    onExecutionSessionStateChange(payload);
  }, [
    selectedModel,
    testChats,
    copySuccess,
    creatorUpdatesDone,
    introSuppressedOnce,
    onExecutionSessionStateChange,
    isExecuting,
    isSending,
    getExecutionSessionKey,
  ]);

  useEffect(() => {
    onExecutingChange?.(isExecuting || isSending || isBuilderBusy);
  }, [isExecuting, isSending, isBuilderBusy, onExecutingChange]);

  // When parent opens Execute panel and sets runExecuteOnOpen, auto-submit prompt + context once
  useEffect(() => {
    if (!runExecuteOnOpen) {
      autoExecutedRef.current = false;
      return;
    }
    if (!selectedModel || isExecuting || autoExecutedRef.current) return;
    const model = selectedModel || 'gpt-6-sol';
    if (!model) return;
    autoExecutedRef.current = true;
    setIntroSuppressedOnce(true);
    handleExecute();
    onExecuteTriggered?.();
  }, [runExecuteOnOpen, selectedModel]); // eslint-disable-line react-hooks/exhaustive-deps -- handleExecute stable enough; only run when runExecuteOnOpen flips true

  const modelsForDropdown = useMemo(
    () => CHAT_AVAILABLE_MODELS.filter((m) => m.type === 'chat'),
    []
  );

  const sortedModels = useMemo(() => {
    const models = [...modelsForDropdown];
    if (recommendedModelId && models.some((m) => m.id === recommendedModelId)) {
      models.sort((a, b) =>
        a.id === recommendedModelId ? -1 : b.id === recommendedModelId ? 1 : 0
      );
    }
    return models;
  }, [modelsForDropdown, recommendedModelId]);

  const executeChatSystemPrompt = useMemo(
    () => buildExecuteChatSystemPrompt({
      title: prompt?.title || prompt?.name || '',
      description: prompt?.description || '',
      capabilityType: prompt?.capabilityType || '',
      howToUse: prompt?.howToUse || '',
      whereToUse: prompt?.whereToUse || '',
      usagePlatform: prompt?.usagePlatform || '',
      usagePlatformOther: prompt?.usagePlatformOther || '',
      platformLabel: formatUsagePlatformLabel({
        whereToUse: prompt?.whereToUse,
        usagePlatform: prompt?.usagePlatform,
        usagePlatformOther: prompt?.usagePlatformOther,
      }),
    }),
    [
      prompt?.title,
      prompt?.name,
      prompt?.description,
      prompt?.capabilityType,
      prompt?.howToUse,
      prompt?.whereToUse,
      prompt?.usagePlatform,
      prompt?.usagePlatformOther,
    ]
  );

  useEffect(() => {
    if (!selectedModelProp && !selectedModelInternal) {
      setSelectedModelInternal(recommendedModelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedModelId]);

  const getSelectedContexts = useCallback((extraContexts = []) => {
    const source = Array.isArray(allContexts) && allContexts.length > 0 ? allContexts : (prompt?.contexts || []);
    const merged = [...source];
    (extraContexts || []).forEach((context) => {
      if (context?.id && !merged.some((item) => String(item?.id) === String(context.id))) merged.push(context);
    });
    return merged.filter((context) => context?.id != null && includedContexts?.[context.id] !== false);
  }, [allContexts, prompt?.contexts, includedContexts]);

  const getLiveContextRequestData = useCallback((promptPart, extraContexts = []) => {
    const selectedContexts = getSelectedContexts(extraContexts);
    const persistedContexts = [];
    const temporaryContexts = [];

    (selectedContexts || []).forEach((context) => {
      const contextId = String(context?.id || '');
      if (contextId.startsWith('temp-')) {
        temporaryContexts.push(context);
      } else if (context?.id) {
        persistedContexts.push(context);
      }
    });

    const contextIds = persistedContexts.map((c) => c.id).filter(Boolean);
    const useContextIds = contextIds.length > 0;
    const categoryMap = { ...(prompt?.contextCategories || {}), ...contextCategories };
    const serializedTemporaryContexts = temporaryContexts
      .map((context) => serializeTemporaryContext(context, categoryMap))
      .filter(Boolean);
    const useExecutionContexts = useContextIds || serializedTemporaryContexts.length > 0;

    return {
      contextIds,
      useContextIds,
      temporaryContexts: serializedTemporaryContexts,
      useExecutionContexts,
      promptForExecution: (promptPart || '').toString(),
    };
  }, [getSelectedContexts, prompt?.contextCategories, contextCategories]);

  const getFullExecutionText = useCallback(() => {
    // Always prefer whatever is currently visible/edited in the dialog.
    const promptBody =
      (editedPromptText && editedPromptText.trim().length > 0
        ? editedPromptText
        : (constructedPrompt || prompt?.content || prompt?.prompt || '')
      ).toString();

    // Always send full text (context + prompt) to the API. Parent provides fullPromptText.
    if (fullPromptText && fullPromptText.trim() !== '') {
      return fullPromptText;
    }
    const selectedContexts = getSelectedContexts();
    const promptPart = promptBody || '';
    const categoryMap = { ...(prompt?.contextCategories || {}), ...contextCategories };
    return buildFullExecutionPayload({
      contexts: selectedContexts,
      contextCategoryById: categoryMap,
      promptPart,
    }).trim();
  }, [fullPromptText, prompt, constructedPrompt, editedPromptText, getSelectedContexts, contextCategories]);

  const getPromptOnlyText = useCallback(() => {
    // Same base logic as getFullExecutionText, but without context wrapper.
    const promptBody =
      (promptOnlyText && promptOnlyText.trim().length > 0
        ? promptOnlyText
        : (editedPromptText && editedPromptText.trim().length > 0
          ? editedPromptText
          : (constructedPrompt || prompt?.content || prompt?.prompt || ''))
      ).toString();

    return promptBody.trim();
  }, [promptOnlyText, editedPromptText, constructedPrompt, prompt]);

  const buildWorkspaceExport = useCallback((workspaceText = '', workspacePrompt = null) => {
    const selectedContexts = getSelectedContexts();
    const targetPrompt = workspacePrompt || prompt || {};
    const title = targetPrompt?.title || targetPrompt?.name || prompt?.title || prompt?.name || 'PromptCrew workspace';
    const capabilityText = String(
      targetPrompt?.content
      || targetPrompt?.prompt
      || getPromptOnlyText()
      || workspaceText
      || ''
    ).trim();
    const executionText = String(workspaceText || '').trim() || getFullExecutionText();
    const categoryMap = {
      ...(targetPrompt?.contextCategories || {}),
      ...(prompt?.contextCategories || {}),
      ...contextCategories,
    };
    const contextSections = selectedContexts.map((ctx) => {
      const folder = categoryMap[ctx.id] || ctx.category || 'Knowledge';
      const content = String(ctx.content || ctx.description || '').trim();
      return [
        `## ${ctx.name || PRODUCT_TERMS.knowledge.untitled}`,
        `Folder: ${folder}`,
        ctx.sourceUrl ? `Source URL: ${ctx.sourceUrl}` : '',
        ctx.sourceFileName ? `Source file: ${ctx.sourceFileName}` : '',
        '',
        content,
      ].filter((line) => line !== '').join('\n');
    });
    const usedCtxNames = new Map();
    const knowledgeFileEntries = selectedContexts.map((ctx) => {
      const folder = sanitizeWorkspacePathSegment(categoryMap[ctx.id] || ctx.category || 'knowledge', 'knowledge');
      const originalName = ctx.sourceFileName || ctx.name || 'knowledge.md';
      const extensionMatch = String(originalName).match(/(\.[A-Za-z0-9_-]+)$/);
      const extension = extensionMatch ? extensionMatch[1] : '.md';
      const base = sanitizeWorkspacePathSegment(String(originalName).replace(/\.[^.]+$/, '') || ctx.name || 'knowledge', 'knowledge');
      const key = `${folder}/${base}`;
      const count = (usedCtxNames.get(key) || 0) + 1;
      usedCtxNames.set(key, count);
      const fileName = count > 1 ? `${base}-${count}${extension}` : `${base}${extension}`;
      return { ctx, folder, fileName, path: `${folder}/${fileName}` };
    });
    const markdown = [
      `# ${title}`,
      '',
      'This export is a PromptCrew handoff workspace.',
      '',
      '## Files',
      '',
      '- capability.md - the capability instructions to paste or import into the target platform.',
      ...knowledgeFileEntries.map((entry) => `- ${entry.path} - attached knowledge/reference material.`),
      '',
      '## Run notes',
      '',
      'Use capability.md with the attached knowledge files. If the target platform supports workspace or MCP import, import the full folder; otherwise paste capability.md and attach the knowledge files manually.',
    ].filter((part) => String(part || '').trim()).join('\n\n');
    const clipboardMarkdown = [
      `# ${title}`,
      '',
      '## Capability',
      '',
      capabilityText || executionText,
      '',
      selectedContexts.length ? '## Attached knowledge' : '',
      '',
      ...contextSections,
    ].filter((part) => String(part || '').trim()).join('\n\n');
    return {
      title,
      text: capabilityText || executionText,
      executionText,
      markdown,
      clipboardMarkdown,
      contexts: selectedContexts,
      knowledgeFileEntries,
      categoryMap,
    };
  }, [contextCategories, getFullExecutionText, getPromptOnlyText, getSelectedContexts, prompt]);

  const copyWorkspaceToClipboard = useCallback(async (workspaceText = '', workspacePrompt = null) => {
    const { clipboardMarkdown, markdown } = buildWorkspaceExport(workspaceText, workspacePrompt);
    const copyMarkdown = clipboardMarkdown || markdown;
    const text = String(copyMarkdown || '').trim();
    if (!text) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    }
  }, [buildWorkspaceExport]);

  const downloadWorkspaceZip = useCallback(async (workspaceText = '', workspacePrompt = null) => {
    if (typeof document === 'undefined') return;
    const workspace = buildWorkspaceExport(workspaceText, workspacePrompt);
    if (!workspace.markdown) return;
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    const rootName = sanitizeWorkspacePathSegment(workspace.title, 'promptcrew-workspace');
    const root = zip.folder(rootName);
    root.file('workspace.md', workspace.markdown);
    root.file('capability.md', workspace.text || '');
    workspace.knowledgeFileEntries.forEach(({ ctx, folder, fileName }) => {
      const dir = root.folder(folder);
      dir.file(fileName, String(ctx.content || ctx.description || ''));
    });
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${safeDownloadName(rootName)}.zip`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [buildWorkspaceExport]);

  const responseFollowRequestRef = useRef(false);
  const onDiscoveryAction = useCallback((payload) => {
    if (['refine-capability', 'build', 'find-knowledge', 'run'].includes(payload.action)) responseFollowRequestRef.current = true;
    return dispatchDiscoveryAction?.(payload);
  }, [dispatchDiscoveryAction]);
  const onBuilderAction = useCallback((payload) => {
    if (['confirm-generate', 'generate-refined', 'apply-generated', 'knowledge-done', 'finalize-generate', 'run-generated-capability'].includes(payload.action)) responseFollowRequestRef.current = true;
    return dispatchBuilderAction?.(payload);
  }, [dispatchBuilderAction]);

  const handleWorkflowActionClick = useCallback((action, message) => {
    if (action?.action === 'connect-mcp' || action?.action === 'use-in-ai-tool') {
      const capability = message?.workflowActions?.prompt || prompt || {};
      navigator.clipboard.writeText(buildCapabilityMcpHandoff({ ...capability, title: capability.title || capability.name || 'Untitled capability', executionText: message?.workflowActions?.workspaceText }, window.location.origin))
        .then(() => setWorkspaceActionStatus({ open: true, message: 'Copied. Paste this message into your integrated AI tool and send it.', severity: 'success' }))
        .catch(() => setWorkspaceActionStatus({ open: true, message: 'Failed to copy', severity: 'error' }));
      return;
    }
    if (action?.action === 'copy-workspace') {
      copyWorkspaceToClipboard(message?.workflowActions?.workspaceText, message?.workflowActions?.prompt)
        .then(() => {
          setCopySuccess((prev) => ({ ...prev, [message.id]: true }));
          setWorkspaceActionStatus({ open: true, message: 'Workspace copied with attached knowledge', severity: 'success' });
          setTimeout(() => {
            setCopySuccess((prev) => ({ ...prev, [message.id]: false }));
          }, 1600);
        })
        .catch((error) => setWorkspaceActionStatus({ open: true, message: error?.message || 'Copy failed', severity: 'error' }));
      return;
    }
    if (action?.action === 'download-workspace') {
      downloadWorkspaceZip(
        message?.workflowActions?.workspaceText,
        message?.workflowActions?.prompt
      )
        .then(() => setWorkspaceActionStatus({ open: true, message: 'Workspace download started', severity: 'success' }))
        .catch((error) => setWorkspaceActionStatus({ open: true, message: error?.message || 'Download failed', severity: 'error' }));
      return;
    }
    if (action?.action === 'run-anyways') {
      const workflow = message?.workflowActions || {};
      const workspaceText = workflow.workspaceText || '';
      const promptMeta = workflow.prompt || prompt || {};
      const actionMode = workflow.executeMode || executeMode || 'chat';
      const platformLabel = formatUsagePlatformLabel({
        whereToUse: promptMeta?.whereToUse,
        usagePlatform: promptMeta?.usagePlatform,
        usagePlatformOther: promptMeta?.usagePlatformOther,
      });
      const promptTitle = promptMeta?.title || promptMeta?.name || 'this capability';
      const runAnywayMessage = [
        actionMode === 'creator'
          ? `Run "${promptTitle}" in PromptCrew Creator as a best-effort artifact version.`
          : `Run "${promptTitle}" in PromptCrew Chat as a best-effort chatbot version.`,
        'Use the attached workspace/context privately to produce the requested output now.',
        actionMode === 'creator'
          ? 'Do not explain that PromptCrew Creator is not the right fit unless no useful artifact can be produced.'
          : 'Do not explain that PromptCrew is not the right fit unless no useful chat-safe output is possible.',
        '',
        workspaceText,
      ].join('\n');
      handleSendMessageRef.current?.(selectedModel || 'gpt-6-sol', runAnywayMessage, {
        skipPreflight: true,
        runAnyway: true,
        visibleUserMessage: getRunAnywayLabel(actionMode),
        systemPromptOverride: actionMode === 'creator'
          ? buildRunAnywayCreatorPrompt({ prompt: promptMeta, platformLabel })
          : buildRunAnywaySystemPrompt({ prompt: promptMeta, platformLabel }),
      });
      return;
    }
    if (action?.action === 'refine-capability') {
      onDiscoveryAction?.({
        action: action.action,
        query: (message.workflowActions || message).query,
        prompt: (message.workflowActions || message).prompt,
        instruction: '__request_input__',
        message,
      });
      return;
    }
    onDiscoveryAction?.({
      action: action.action,
      query: (message.workflowActions || message).query,
      prompt: (message.workflowActions || message).prompt,
      message,
    });
  }, [copyWorkspaceToClipboard, downloadWorkspaceZip, executeMode, onDiscoveryAction, prompt, selectedModel]);

  const promptStats = useMemo(() => {
    const text = getFullExecutionText();
    const characters = text.length;
    // rough heuristic
    const estimatedTokens = Math.ceil(characters / 4);
    return { characters, estimatedTokens };
  }, [getFullExecutionText]);

  const ensureModelsToTest = useCallback(() => [selectedModel].filter(Boolean), [selectedModel]);

  const executePromptFlow = useCallback(async () => {
    const modelsToTest = ensureModelsToTest();
    if (modelsToTest.length === 0) return;

    if (isExecuting) return;

    abortRequestedRef.current = false;
    setIsExecuting(true);
    setIsSending(true);
    setSendingStatusLabel('Prompt execution in progress...');

    const overrideSnap = executionPayloadOverrideRef?.current;
    if (executionPayloadOverrideRef) {
      executionPayloadOverrideRef.current = null;
    }

    const promptText =
      overrideSnap?.fullPayload && String(overrideSnap.fullPayload).trim() !== ''
        ? overrideSnap.fullPayload
        : getFullExecutionText();

    const pendingChats = {};
    modelsToTest.forEach((model) => {
      const previousMessages = preserveConversationOnExecute ? (chatsRef.current[model] || []) : [];
      pendingChats[model] = [
        ...previousMessages,
        {
          id: Date.now(),
          type: 'user',
          content: promptText,
          timestamp: new Date(),
        },
      ];
    });
    setTestChats(pendingChats);

    if (onLockPrompt) {
      onLockPrompt();
    }

    if (!isPromptCrewRunnable(prompt, executeMode)) {
      const platformLabel = formatUsagePlatformLabel({
        whereToUse: prompt?.whereToUse,
        usagePlatform: prompt?.usagePlatform,
        usagePlatformOther: prompt?.usagePlatformOther,
      });
      const handoffMessage = {
        id: `${Date.now()}-promptcrew-handoff`,
        type: 'assistant',
        content: buildPromptCrewHandoffMessage({ prompt, platformLabel, executeMode }),
        workflowActions: {
          prompt,
          executeMode,
          workspaceText: promptText,
          actions: buildPromptCrewHandoffActions(executeMode),
        },
        timestamp: new Date(),
      };
      const handoffChats = {};
      modelsToTest.forEach((model) => {
        handoffChats[model] = [
          ...(pendingChats[model] || []),
          handoffMessage,
        ];
      });
      setTestChats(handoffChats);
      setIsExecuting(false);
      setIsSending(false);
      setSendingStatusLabel('AI is thinking...');
      return;
    }

    const promptOnlyForContexts =
      overrideSnap?.promptOnlySubstituted != null
        ? String(overrideSnap.promptOnlySubstituted)
        : getPromptOnlyText();

    const {
      contextIds,
      temporaryContexts,
      useExecutionContexts,
      promptForExecution,
    } = getLiveContextRequestData(promptOnlyForContexts);

    const basePromptForDetection =
      (constructedPrompt || prompt?.content || prompt?.prompt || '').trim();
    const longFormRequested = isCreatorLongFormPrompt(basePromptForDetection);

    const newChats = pendingChats;

    for (const model of modelsToTest) {
      let lastChatMessages = [...(newChats[model] || [])];
      try {
        let messageHistory = (newChats[model] || []).map((m) => ({
          role: m.type === 'assistant' ? 'assistant' : 'user',
          content: String(useExecutionContexts && m.type === 'user' ? (promptForExecution ?? '') : (m.content ?? '')),
        }));
        const isCreator = executeMode === 'creator' && typeof onCreatorDocumentGenerated === 'function';
        if (isCreator) {
          messageHistory = [{ role: 'system', content: CREATOR_SYSTEM_PROMPT }, ...messageHistory];
        } else if (executeMode === 'chat') {
          messageHistory = [{ role: 'system', content: executeChatSystemPrompt }, ...messageHistory];
        }

        const maxTokens = executeMode === 'creator'
          ? getCreatorMaxTokens(basePromptForDetection)
          : (longFormRequested ? Math.max(CHAT_MAX_TOKENS, 2600) : CHAT_MAX_TOKENS);
        let continueRound = 0;
        let accumulatedDocument = '';
        let lastContinuationReason = null;
        let chatContinueRound = 0;

        for (;;) {
          if (abortRequestedRef.current) break;
          let isQuestion = false;

          const result = await executePromptApi({
            model,
            messageHistory,
            promptId: prompt?.id,
            recordUsage: continueRound === 0 && chatContinueRound === 0,
            contextIds: contextIds.length > 0 ? contextIds : undefined,
            temporaryContexts: temporaryContexts.length > 0 ? temporaryContexts : undefined,
            max_tokens: maxTokens,
          });

          const assistantContent = result?.message?.content || '';
          const contentString = typeof assistantContent === 'string'
            ? assistantContent
            : typeof assistantContent === 'object'
              ? JSON.stringify(assistantContent)
              : String(assistantContent || '');

          if (contentString) {
            let chatContent = contentString;
            if (isCreator) {
              const qParsed = parseCreatorQuestion(contentString);
              const questionText = qParsed?.question;
              if (questionText || isModelAskingQuestion(contentString)) {
                isQuestion = true;
                chatContent = questionText || contentString.trim();
              } else {
                const parsed = parseCreatorDocumentResponse(contentString);
                if (parsed && parsed.document) {
                  if (continueRound === 0) {
                    accumulatedDocument = parsed.document;
                  } else {
                    const newSection = parsed.document.trim();
                    accumulatedDocument = accumulatedDocument
                      ? (accumulatedDocument + '\n\n' + newSection).trim()
                      : newSection;
                  }
                  onCreatorDocumentGenerated(accumulatedDocument, parsed.explanation);
                  chatContent = creatorChatSummary(parsed, contentString);
                } else {
                  const restatedCapability = looksLikeRestatedCapabilityResponse(contentString, prompt);
                  if (restatedCapability) {
                    chatContent = 'I could not turn this capability into a Creator artifact from the available details. Add the specific input or goal you want turned into a document, or use Run anyway in Creator to create a best-effort static artifact.';
                  } else {
                    accumulatedDocument = (accumulatedDocument + '\n' + contentString.trim()).trim();
                    onCreatorDocumentGenerated(accumulatedDocument, '');
                    chatContent = creatorChatSummary(null, contentString);
                  }
                }
              }
            }
            if (chatContent) {
              const contextDiagnosticsSummary = summarizeContextDiagnostics(result?.contextDiagnostics);
              const creatorDiagnosticsSummary = summarizeCreatorDiagnostics({
                continuationReason: lastContinuationReason,
                round: continueRound,
                maxTokens,
              });
              const lastMessageIndex = lastChatMessages.length - 1;
              const shouldAppendChatContinuation = !isCreator && chatContinueRound > 0 && lastChatMessages[lastMessageIndex]?.type === 'assistant';
              lastChatMessages = shouldAppendChatContinuation
                ? lastChatMessages.map((message, index) => (
                  index === lastMessageIndex
                    ? { ...message, content: `${message.content}\n\n${chatContent}`.trim(), contextDiagnostics: contextDiagnosticsSummary }
                    : message
                ))
                : [
                  ...lastChatMessages,
                  {
                    id: Date.now() + lastChatMessages.length,
                    type: 'assistant',
                    content: chatContent,
                    contextDiagnostics: contextDiagnosticsSummary,
                    creatorDiagnostics: creatorDiagnosticsSummary,
                    timestamp: new Date(),
                  },
                ];
              setTestChats((prev) => ({ ...prev, [model]: lastChatMessages }));
            }
            messageHistory = [...messageHistory, { role: 'assistant', content: contentString }];
          }

          const apiTruncated = result?.finishReason === 'length' || result?.finishReason === 'max_output_tokens';
          if (!isCreator && apiTruncated && chatContinueRound < MAX_CHAT_CONTINUE_ROUNDS && !abortRequestedRef.current) {
            messageHistory = [...messageHistory, { role: 'user', content: buildChatContinuePrompt() }];
            chatContinueRound += 1;
            continue;
          }
          const hasExplanation = contentString.includes('---EXPLANATION---');
          const parsedStatus = parseCreatorStatus(contentString);
          const decision = getCreatorContinuationDecision({
            isCreator,
            isQuestion,
            status: parsedStatus,
            finishReason: result?.finishReason,
            hasDocument: contentString.includes('---DOCUMENT---'),
            hasExplanation,
            contentLength: contentString.length,
            continueRound,
            maxContinueRounds: MAX_CREATOR_CONTINUE_ROUNDS,
          });
          lastContinuationReason = decision.reason;
          if (apiTruncated) lastContinuationReason = 'finish_length';
          if (abortRequestedRef.current || !decision.shouldContinue) {
            if (decision.reason === 'max_rounds' && lastChatMessages.length > 0) {
              const cappedSummary = summarizeCreatorDiagnostics({
                continuationReason: 'max_rounds',
                round: continueRound,
                maxTokens,
              });
              lastChatMessages = lastChatMessages.map((message, index) => (
                index === lastChatMessages.length - 1
                  ? { ...message, creatorDiagnostics: cappedSummary }
                  : message
              ));
              setTestChats((prev) => ({ ...prev, [model]: lastChatMessages }));
            }
            break;
          }

          await new Promise((r) => setTimeout(r, 50));

          const continuePrompt = buildCreatorContinuePrompt(accumulatedDocument);
          messageHistory = [...messageHistory, { role: 'user', content: continuePrompt }];
          continueRound += 1;
        }
      } catch (e) {
        setTestChats((prev) => ({
          ...prev,
          [model]: [
            ...(prev[model] || []),
            {
              id: Date.now() + 2,
              type: 'assistant',
              content: `Error executing capability: ${e?.message || 'Unknown error'}`,
              timestamp: new Date(),
            },
          ],
        }));
      }

      const isCreatorDone = executeMode === 'creator' && typeof onCreatorDocumentGenerated === 'function';
      if (isCreatorDone && !abortRequestedRef.current && lastChatMessages.length > 0) {
        setCreatorUpdatesDone(true);
      }
    }

    setIsExecuting(false);
    setIsSending(false);
    setSendingStatusLabel('AI is thinking...');
  }, [
    isExecuting,
    onLockPrompt,
    getFullExecutionText,
    getPromptOnlyText,
    getLiveContextRequestData,
    constructedPrompt,
    prompt,
    executeMode,
    onCreatorDocumentGenerated,
    executionPayloadOverrideRef,
    ensureModelsToTest,
    executeChatSystemPrompt,
    preserveConversationOnExecute,
  ]);

  const handleExecute = useCallback(() => {
    const modelsToTest = ensureModelsToTest();
    if (modelsToTest.length === 0) return;
    if (isExecuting) return;
    if (typeof wrapExecuteAction === 'function') {
      wrapExecuteAction(() => {
        void executePromptFlow();
      });
      return;
    }
    void executePromptFlow();
  }, [wrapExecuteAction, executePromptFlow, isExecuting, ensureModelsToTest]);

  const handleStopExecution = useCallback(() => {
    abortRequestedRef.current = true;
  }, []);

  const handleRestartSession = useCallback(() => {
    setTestChats({});
    lastOutgoingSessionRef.current = null;
    lastOutgoingSessionKeyRef.current = '';
    autoExecutedRef.current = false;
    setIntroSuppressedOnce(false);
    setCopySuccess({});
    setIsExecuting(false);
    setIsSending(false);
    setCreatorUpdatesDone(false);
    abortRequestedRef.current = false;
    if (messageInputRef.current) {
      messageInputRef.current.value = '';
    }
  }, []);

  // Removed auto-execute - user must manually click "Execute" button

  const currentModelKey = selectedModel;
  const currentMessages = useMemo(() => testChats[currentModelKey] || [], [testChats, currentModelKey]);
  const latestActionMessageId = useMemo(() => [...currentMessages].reverse()
    .find((message) => message.type === 'assistant' && (message.builderFlow || message.discovery || message.workflowActions))?.id,
  [currentMessages]);

  const handleConversationScroll = useConversationScroll({
    messages: currentMessages,
    assistant: isAssistantChatUi,
    containerRef: chatContainerRef,
    endRef: chatEndRef,
    busy: isExecuting || isSending,
    requestFollowRef: responseFollowRequestRef,
  });

  const addGeneratedTemporaryContexts = useCallback(async (contexts) => {
    const validContexts = (contexts || []).filter((context) => context?.id && String(context?.content || '').trim());
    if (!validContexts.length) return [];
    await Promise.resolve(onCreateTemporaryContexts?.(validContexts));
    onTemporaryContextsCreated?.(validContexts);
    return validContexts;
  }, [onCreateTemporaryContexts, onTemporaryContextsCreated]);

  const buildTemporaryContextFromFile = useCallback(async (file) => {
    const extension = getFileExtension(file.name);
    const isRaw = RAW_TEMP_CONTEXT_EXTENSIONS.has(extension) || RAW_TEMP_CONTEXT_EXTENSIONS.has(String(file.name).toLowerCase());
    let content = '';
    if (isRaw) {
      content = await file.text();
    } else {
      const base64 = await readFileAsDataUrl(file);
      const parsed = await parseDocument(base64, file.name, file.type || extension);
      content = parsed?.text || '';
    }
    return {
      id: tempContextId(),
      name: stripFileExtension(file.name),
      content,
      category: 'Temporary',
      hasAccess: true,
      source: 'UPLOADED_FILE',
      sourceFileName: file.name,
      sourceFileType: file.type || extension,
    };
  }, []);

  const buildTemporaryContextFromUrl = useCallback(async (url) => {
    const rawUrl = String(url || '').trim();
    if (!rawUrl) return null;
    try {
      const result = await fetchWebContext(rawUrl);
      const resolvedUrl = result?.resolvedUrl || result?.url || rawUrl;
      const title = result?.metadata?.title || resolvedUrl;
      const fetchedText = result?.text || '';
      return {
        id: tempContextId(),
        name: title || 'Web knowledge',
        content: fetchedText ? `Source URL: ${resolvedUrl}\n\n${fetchedText}` : `Source URL: ${resolvedUrl}`,
        category: 'Temporary',
        hasAccess: true,
        source: 'WEB_URL',
        sourceUrl: resolvedUrl,
        sourceFileType: result?.fileType || 'text/html',
      };
    } catch {
      return {
        id: tempContextId(),
        name: rawUrl,
        content: `Source URL: ${rawUrl}`,
        category: 'Temporary',
        hasAccess: true,
        source: 'WEB_URL',
        sourceUrl: rawUrl,
        sourceFileType: 'text/html',
      };
    }
  }, []);

  const createTemporaryContextsFromFiles = useCallback(async (files) => {
    if (!onCreateTemporaryContexts) return [];
    const fileList = Array.from(files || []);
    if (!fileList.length) return [];
    setIsPreparingContext(true);
    setContextPreparationLabel(fileList.length === 1 ? `Adding file as ${PRODUCT_TERMS.temporaryKnowledge}...` : `Adding ${fileList.length} files as ${PRODUCT_TERMS.temporaryKnowledge}...`);
    try {
      const contexts = [];
      for (const file of fileList) {
        try {
          const context = await buildTemporaryContextFromFile(file);
          if (context?.content) contexts.push(context);
        } catch (error) {
          console.error('Error creating temporary context from dropped file:', error);
        }
      }
      return addGeneratedTemporaryContexts(contexts);
    } finally {
      setIsPreparingContext(false);
      setContextPreparationLabel('');
    }
  }, [addGeneratedTemporaryContexts, buildTemporaryContextFromFile, onCreateTemporaryContexts]);

  const createTemporaryContextsFromUrls = useCallback(async (message) => {
    if (!onCreateTemporaryContexts) return [];
    const urls = Array.from(new Set(String(message || '').match(URL_REGEX) || []));
    if (!urls.length) return [];
    setIsPreparingContext(true);
    setContextPreparationLabel(urls.length === 1 ? `Loading link as ${PRODUCT_TERMS.temporaryKnowledge}...` : `Loading ${urls.length} links as ${PRODUCT_TERMS.temporaryKnowledge}...`);
    try {
      const contexts = [];
      for (const url of urls) {
        const context = await buildTemporaryContextFromUrl(url);
        if (context) contexts.push(context);
      }
      return addGeneratedTemporaryContexts(contexts);
    } finally {
      setIsPreparingContext(false);
      setContextPreparationLabel('');
    }
  }, [addGeneratedTemporaryContexts, buildTemporaryContextFromUrl, onCreateTemporaryContexts]);

  const hasDraggedFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

  const handleDragEnter = useCallback((event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    if (!hasDraggedFiles(event)) return;
    const currentTarget = event.currentTarget;
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && currentTarget?.contains?.(relatedTarget)) return;
    setIsDragActive(false);
  }, []);

  const handleDropFiles = useCallback(async (event) => {
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;
    event.preventDefault();
    setIsDragActive(false);
    await createTemporaryContextsFromFiles(files);
  }, [createTemporaryContextsFromFiles]);

  const submitComposerMessage = (modelKey) => {
    const value = messageInputRef.current?.value?.trim() || '';
    if (!value || isPreparingContext) return;
    if (builderSessionActive && !builderComposerLocked && typeof onBuilderComposerSubmit === 'function') {
      const handled = onBuilderComposerSubmit(value);
      if (handled) {
        if (messageInputRef.current) messageInputRef.current.value = '';
        return;
      }
    }
    handleSendMessage(modelKey, value);
    if (messageInputRef.current) messageInputRef.current.value = '';
  };

  const handleSendMessage = async (model, message, options = {}) => {
    if (!message || isPreparingContext) return;
    if (!options.silentUserMessage && assistantLandingEnabled && typeof onAssistantLandingDismiss === 'function') {
      onAssistantLandingDismiss();
    }
    const generatedContexts = await createTemporaryContextsFromUrls(message);

    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: options.visibleUserMessage || message,
      apiContent: message,
      timestamp: new Date(),
    };

    if (!options.silentUserMessage) {
      setTestChats((prev) => ({
        ...prev,
        [model]: [
          ...(prev[model] || []),
          userMsg,
        ],
      }));
    }

    let promptMetaForRun = options.promptMeta || null;
    abortRequestedRef.current = false;
    setIsSending(true);
    setSendingStatusLabel(typeof onBeforeExecuteMessage === 'function'
      ? 'AI is searching existing capabilities...'
      : 'AI is thinking...');
    setCreatorUpdatesDone(false);

    const current = (chatsRef.current[model] || []).concat([userMsg]);

    if (!options.skipPreflight && typeof onBeforeExecuteMessage === 'function') {
      try {
        const preflight = await onBeforeExecuteMessage({ model, message, userMessage: userMsg, options });
        if (!preflight?.handled && preflight?.execution) {
          message = preflight.execution.message || message;
          promptMetaForRun = preflight.execution.promptMeta || promptMetaForRun;
          setTestChats((prev) => ({ ...prev, [model]: (prev[model] || []).map((item) => (
            item.id === userMsg.id ? { ...item, apiContent: message } : item
          )) }));
        }
        if (preflight?.handled) {
          const assistantMessages = Array.isArray(preflight.messages) ? preflight.messages : [];
          if (assistantMessages.length > 0) {
            setTestChats((prev) => ({
              ...prev,
              [model]: [
                ...(prev[model] || []),
                ...assistantMessages.map((item, index) => ({
                  id: item.id || `${Date.now()}-assistant-${index}`,
                  type: 'assistant',
                  timestamp: item.timestamp || new Date(),
                  ...item,
                })),
              ],
            }));
          }
          setIsSending(false);
          setSendingStatusLabel('AI is thinking...');
          return;
        }
      } catch (preflightError) {
        setTestChats((prev) => ({
          ...prev,
          [model]: [
            ...(prev[model] || []),
            {
              id: `${Date.now()}-assistant-error`,
              type: 'assistant',
              content: preflightError?.message || 'Assistant discovery failed. You can still continue with normal chat.',
              timestamp: new Date(),
            },
          ],
        }));
        setIsSending(false);
        setSendingStatusLabel('AI is thinking...');
        return;
      }
    }

    if (promptMetaForRun && !options.runAnyway && !isPromptCrewRunnable(promptMetaForRun, executeMode)) {
      const platformLabel = formatUsagePlatformLabel(promptMetaForRun);
      setTestChats((prev) => ({
        ...prev,
        [model]: [...(prev[model] || []), {
          id: `${Date.now()}-promptcrew-handoff`, type: 'assistant', timestamp: new Date(),
          content: buildPromptCrewHandoffMessage({ prompt: promptMetaForRun, platformLabel, executeMode }),
          workflowActions: {
            prompt: promptMetaForRun, executeMode, workspaceText: message,
            actions: buildPromptCrewHandoffActions(executeMode),
          },
        }],
      }));
      setIsSending(false);
      return;
    }
    setSendingStatusLabel('AI is thinking...');

    let lastChatMessages = [...current];
    const isCreatorVibe = executeMode === 'creator' && typeof onCreatorDocumentGenerated === 'function';
    const {
      contextIds,
      temporaryContexts,
      promptForExecution,
    } = getLiveContextRequestData(message, generatedContexts);

    try {
      // Use latest history (avoid stale closure) + the just-appended user message
      let messageHistory = current.map((m) => ({
        role: m.type === 'assistant' ? 'assistant' : 'user',
        content: m.id === userMsg.id
          ? promptForExecution
          : (m.apiContent || m.content),
      }));

      if (isCreatorVibe) {
        const useEditPrompt = Boolean(creatorDocument && String(creatorDocument).trim());
        messageHistory = [{
          role: 'system',
          content: options.systemPromptOverride || (useEditPrompt ? CREATOR_EDIT_SYSTEM_PROMPT : CREATOR_SYSTEM_PROMPT),
        }, ...messageHistory];
        if (creatorDocument && creatorDocument.trim()) {
          const lastIdx = messageHistory.length - 1;
          const { mapText } = buildDocumentBlockMap(creatorDocument);
          messageHistory[lastIdx] = {
            role: 'user',
            content: `${mapText}\n\nCurrent document:\n\`\`\`markdown\n${creatorDocument}\n\`\`\`\n\nRequested changes: ${promptForExecution}`,
          };
        }
      } else if (executeMode === 'chat') {
        messageHistory = [{
          role: 'system',
          content: options.systemPromptOverride || executeChatSystemPrompt,
        }, ...messageHistory];
      }
      let continueRound = 0;
      let chatContinueRound = 0;
      let accumulatedDocument = (creatorDocument && String(creatorDocument).trim()) || '';
      let lastResponseWasFull = false;
      let lastContinuationReason = null;
      const creatorMaxTokens = getCreatorMaxTokens(promptForExecution);

      for (;;) {
        if (abortRequestedRef.current) break;
        let isQuestion = false;

        const result = await executePromptApi({
          model,
          messageHistory,
          promptId: promptMetaForRun?.id,
          recordUsage: continueRound === 0 && chatContinueRound === 0,
          contextIds: contextIds.length > 0 ? contextIds : undefined,
          temporaryContexts: temporaryContexts.length > 0 ? temporaryContexts : undefined,
          max_tokens: executeMode === 'creator' ? creatorMaxTokens : CHAT_MAX_TOKENS,
        });

        const assistantContent = result?.message?.content || '';
        const contentString = typeof assistantContent === 'string'
          ? assistantContent
          : typeof assistantContent === 'object'
            ? JSON.stringify(assistantContent)
            : String(assistantContent || '');

        if (contentString) {
          let chatContent = contentString;
          lastResponseWasFull = false;
          if (isCreatorVibe) {
            const qParsed = parseCreatorQuestion(contentString);
            const questionText = qParsed?.question;
            if (questionText || isModelAskingQuestion(contentString)) {
              isQuestion = true;
              chatContent = questionText || contentString.trim();
            } else {
              const editParsed = parseCreatorEditResponse(contentString);
              const hasEditFormat = editParsed && (
                (editParsed.replacements && editParsed.replacements.length > 0) ||
                editParsed.appendContent ||
                editParsed.fullDocument
              );
              if (hasEditFormat) {
                if (editParsed.fullDocument) {
                  accumulatedDocument = editParsed.fullDocument;
                  lastResponseWasFull = true;
                } else {
                  if (editParsed.replacements && editParsed.replacements.length > 0) {
                    const { blocks } = buildDocumentBlockMap(accumulatedDocument);
                    accumulatedDocument = applyBlockReplacements(accumulatedDocument, blocks, editParsed.replacements);
                  }
                  if (editParsed.appendContent) {
                    accumulatedDocument = accumulatedDocument
                      ? (accumulatedDocument + '\n\n' + editParsed.appendContent).trim()
                    : editParsed.appendContent;
                  }
                }
                onCreatorDocumentGenerated(accumulatedDocument, editParsed.explanation || '');
                chatContent = editParsed.explanation && !isGenericExplanation(editParsed.explanation)
                  ? editParsed.explanation
                  : creatorChatSummary({ explanation: editParsed.explanation }, contentString);
              } else {
                const parsed = parseCreatorDocumentResponse(contentString);
                if (parsed && parsed.document) {
                  if (continueRound === 0) {
                    accumulatedDocument = mergeCreatorSection(accumulatedDocument, parsed.document);
                  } else {
                    const newSection = parsed.document.trim();
                    accumulatedDocument = accumulatedDocument
                      ? (accumulatedDocument + '\n\n' + newSection).trim()
                      : newSection;
                  }
                  onCreatorDocumentGenerated(accumulatedDocument, parsed.explanation);
                  chatContent = creatorChatSummary(parsed, contentString);
                } else {
                  const restatedCapability = looksLikeRestatedCapabilityResponse(
                    contentString,
                    promptMetaForRun || prompt
                  );
                  if (restatedCapability) {
                    chatContent = 'I could not turn this capability into a Creator artifact from the available details. Add the specific input or goal you want turned into a document, or use Run anyway in Creator to create a best-effort static artifact.';
                  } else {
                    accumulatedDocument = (accumulatedDocument + '\n' + contentString.trim()).trim();
                    onCreatorDocumentGenerated(accumulatedDocument, '');
                    chatContent = creatorChatSummary(null, contentString);
                  }
                }
              }
            }
          }
          const contextDiagnosticsSummary = summarizeContextDiagnostics(result?.contextDiagnostics);
          const creatorDiagnosticsSummary = summarizeCreatorDiagnostics({
            continuationReason: lastContinuationReason,
            round: continueRound,
            maxTokens: executeMode === 'creator' ? creatorMaxTokens : undefined,
          });
          const lastMessageIndex = lastChatMessages.length - 1;
          const shouldAppendChatContinuation = !isCreatorVibe && chatContinueRound > 0 && lastChatMessages[lastMessageIndex]?.type === 'assistant';
          lastChatMessages = shouldAppendChatContinuation
            ? lastChatMessages.map((message, index) => (
              index === lastMessageIndex
                ? { ...message, content: `${message.content}\n\n${chatContent}`.trim(), contextDiagnostics: contextDiagnosticsSummary }
                : message
            ))
            : [
              ...lastChatMessages,
              {
                id: Date.now() + lastChatMessages.length,
                type: 'assistant',
                content: chatContent,
                contextDiagnostics: contextDiagnosticsSummary,
                creatorDiagnostics: creatorDiagnosticsSummary,
                timestamp: new Date(),
              },
            ];
          setTestChats((prev) => ({ ...prev, [model]: lastChatMessages }));
          messageHistory = [...messageHistory, { role: 'assistant', content: contentString }];
        }

        const apiTruncated = result?.finishReason === 'length' || result?.finishReason === 'max_output_tokens';
        if (!isCreatorVibe && apiTruncated && chatContinueRound < MAX_CHAT_CONTINUE_ROUNDS && !abortRequestedRef.current) {
          messageHistory = [...messageHistory, { role: 'user', content: buildChatContinuePrompt() }];
          chatContinueRound += 1;
          continue;
        }
        const hasExplanation = contentString.includes('---EXPLANATION---');
        const parsedStatus = parseCreatorStatus(contentString);
        const decision = getCreatorContinuationDecision({
          isCreator: isCreatorVibe,
          isQuestion,
          status: parsedStatus,
          finishReason: result?.finishReason,
          hasDocument: contentString.includes('---DOCUMENT---'),
          hasExplanation,
          contentLength: contentString.length,
          continueRound,
          maxContinueRounds: MAX_CREATOR_CONTINUE_ROUNDS,
        });
        lastContinuationReason = decision.reason;
        if (apiTruncated) lastContinuationReason = 'finish_length';
        if (abortRequestedRef.current || !decision.shouldContinue) {
          if (decision.reason === 'max_rounds' && lastChatMessages.length > 0) {
            const cappedSummary = summarizeCreatorDiagnostics({
              continuationReason: 'max_rounds',
              round: continueRound,
              maxTokens: creatorMaxTokens,
            });
            lastChatMessages = lastChatMessages.map((message, index) => (
              index === lastChatMessages.length - 1
                ? { ...message, creatorDiagnostics: cappedSummary }
                : message
            ));
            setTestChats((prev) => ({ ...prev, [model]: lastChatMessages }));
          }
          break;
        }

        await new Promise((r) => setTimeout(r, 50));

        const continuePrompt = lastResponseWasFull
          ? buildCreatorContinueFullPrompt(accumulatedDocument)
          : buildCreatorContinuePrompt(accumulatedDocument);
        messageHistory = [...messageHistory, { role: 'user', content: continuePrompt }];
        continueRound += 1;
      }
    } catch (e) {
      setTestChats((prev) => ({
        ...prev,
        [model]: [
          ...(prev[model] || []),
          {
            id: Date.now() + 2,
            type: 'assistant',
            content: `Error: ${e?.message || 'Unknown error'}`,
            timestamp: new Date(),
          },
        ],
      }));
    } finally {
      // Creator vibe flow: show system "Updates done" below chat instead of a chat bubble
      if (isCreatorVibe && !abortRequestedRef.current && lastChatMessages.length > 0) {
        setCreatorUpdatesDone(true);
      }
      setIsSending(false);
    }
  };
  handleSendMessageRef.current = handleSendMessage;

  useImperativeHandle(ref, () => ({
    execute: handleExecute,
    clearSession: handleRestartSession,
    getSessionSnapshot: () => ({
      selectedModel,
      testChats: chatsRef.current || {},
      copySuccess,
      creatorUpdatesDone,
      introSuppressedOnce,
      composerInput: messageInputRef.current?.value || '',
    }),
    appendAndRun: (message, options = {}) => {
      const text = String(message || '').trim();
      if (!text || isSending || isExecuting) return;
      handleSendMessage(selectedModel || 'gpt-6-sol', text, options);
    },
    getCurrentMessages: () => chatsRef.current[selectedModel || 'gpt-6-sol'] || [],
    appendAssistantMessage: (message) => {
      const modelKey = selectedModel || 'gpt-6-sol';
      const nextMessage = {
        id: message?.id || `${Date.now()}-assistant-manual`,
        type: 'assistant',
        timestamp: message?.timestamp || new Date(),
        content: message?.content || '',
        ...message,
      };
      setTestChats((prev) => ({
        ...prev,
        [modelKey]: [...(prev[modelKey] || []), nextMessage],
      }));
    },
    appendUserMessage: (content) => {
      const modelKey = selectedModel || 'gpt-6-sol';
      const nextMessage = {
        id: `${Date.now()}-user-manual`,
        type: 'user',
        content: String(content || ''),
        timestamp: new Date(),
      };
      setTestChats((prev) => ({
        ...prev,
        [modelKey]: [...(prev[modelKey] || []), nextMessage],
      }));
    },
    updateMessage: (id, patch) => {
      const modelKey = selectedModel || 'gpt-6-sol';
      setTestChats((prev) => ({
        ...prev,
        [modelKey]: (prev[modelKey] || []).map((message) => (
          message.id === id ? { ...message, ...patch } : message
        )),
      }));
    },
    setLandingComposerText: (text) => {
      assistantLandingRef.current?.setComposerText?.(text);
    },
    addTemporaryContextsFromFiles: (files) => createTemporaryContextsFromFiles(files),
  }), [
    handleExecute,
    handleRestartSession,
    handleSendMessage,
    selectedModel,
    copySuccess,
    creatorUpdatesDone,
    introSuppressedOnce,
    isSending,
    isExecuting,
    createTemporaryContextsFromFiles,
  ]);

  if (!prompt) return null;

  const showIntroScreen =
    !suppressIntro &&
    !currentMessages.length &&
    !isExecuting &&
    !isSending &&
    !isPreparingContext;

  const conversationColumnSx = variant === 'shelf'
    ? { width: '100%' }
    : CONVERSATION_COLUMN_SX;

  const isAssistantEmbeddedChat = isAssistantChatUi && variant === 'shelf';

  const assistantContentColumnSx = isAssistantChatUi
    ? {
      width: '100%',
      maxWidth: isAssistantEmbeddedChat ? '100%' : ASSISTANT_CONVERSATION_COLUMN_SX.maxWidth,
      mx: isAssistantEmbeddedChat ? 0 : 'auto',
      px: 2,
      boxSizing: 'border-box',
    }
    : null;

  const assistantComposerColumnSx = isAssistantChatUi
    ? {
      ...assistantContentColumnSx,
      maxWidth: isAssistantEmbeddedChat ? '100%' : ASSISTANT_COMPOSER_MAX_WIDTH,
    }
    : null;

  const threadOuterSx = isAssistantChatUi
    ? {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      bgcolor: ASSISTANT_THREAD_BG,
    }
    : { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' };

  const scrollInnerSx = isAssistantChatUi
    ? { ...assistantContentColumnSx, py: 2 }
    : conversationColumnSx;

  const showAssistantLanding = isAssistantChatUi
    && !suppressAssistantLanding
    && assistantLandingEnabled
    && !runExecuteOnOpen
    && currentMessages.length === 0
    && !isPreparingContext
    && !isChatBusy
    && !isExecuting
    && !isSending
    && !builderSessionActive;

  const showAssistantBusyEmptyState = isAssistantChatUi
    && currentMessages.length === 0
    && !showAssistantLanding
    // A restored or just-cleared builder session can briefly retain its active
    // flag. Only a real request, execution, or status message deserves the
    // full empty-state spinner.
    && (isChatBusy || isExecuting || isSending || runExecuteOnOpen);
  const topStatusLabel = isPreparingContext
    ? (contextPreparationLabel || `Adding ${PRODUCT_TERMS.temporaryKnowledge}...`)
    : (assistantContext === 'builder' && isBuilderBusy ? builderStatusLabel : '');

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDropFiles}
      sx={{
        // Builder supplies its own persistent modal header. Use the remaining
        // flex height so the conversation cannot push that header out of view.
        height: assistantContext === 'builder' ? 'auto' : '100%',
        ...(assistantContext === 'builder' ? { flex: 1, minHeight: 0, overflow: 'hidden' } : {}),
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        bgcolor: isAssistantChatUi ? ASSISTANT_THREAD_BG : undefined,
        ...(variant === 'shelf' ? { minWidth: 0, maxWidth: '100%' } : {})
      }}
    >
      {topStatusLabel && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: (themeValue) => themeValue.zIndex.modal + 2,
            px: 1.5,
            py: 0.75,
            borderRadius: 999,
            bgcolor: 'rgba(239, 246, 255, 0.96)',
            color: 'primary.main',
            border: '1px solid rgba(29, 78, 216, 0.18)',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '0.8125rem',
            fontWeight: 750,
            whiteSpace: 'nowrap',
            maxWidth: 'calc(100% - 32px)',
          }}
        >
          <CircularProgress size={15} color="inherit" />
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {topStatusLabel}
          </Box>
        </Box>
      )}
      <Snackbar
        open={workspaceActionStatus.open}
        autoHideDuration={2600}
        onClose={() => setWorkspaceActionStatus((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 7, zIndex: (themeValue) => themeValue.zIndex.modal + 3 }}
      >
        <Alert
          severity={workspaceActionStatus.severity || 'success'}
          onClose={() => setWorkspaceActionStatus((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {workspaceActionStatus.message}
        </Alert>
      </Snackbar>
      {showHeader && (
        <ExecuteHeaderBar
          executeMode={executeMode}
          onExecuteModeChange={onExecuteModeChange}
          selectedModel={selectedModel}
          onModelChange={(v) => setSelectedModel(v)}
          modelMenuAnchor={modelMenuAnchor}
          onModelMenuAnchorChange={(el) => setModelMenuAnchor(el)}
          prompt={prompt}
        />
      )}
      {/* Pre-execution state: no chat UI, just an Execute button */}
      {showIntroScreen && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 3,
            py: 4,
          }}
        >
          <Stack spacing={2} alignItems="center">
            <Typography variant="body1" fontWeight={500}>
              {executeMode === 'creator' ? 'Ready to create your document?' : 'Ready to run this capability?'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {executeMode === 'creator' ? 'Your document will appear alongside this conversation.' : 'Your response will appear here.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<SendIcon sx={{ fontSize: 16 }} />}
              size="small"
              onClick={handleExecute}
              disabled={isExecuting || isSending}
              sx={{
                minHeight: 36,
                boxShadow: 1,
                bgcolor: (isExecuting || isSending) ? 'action.disabledBackground' : 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: (isExecuting || isSending) ? 'action.disabledBackground' : 'primary.dark',
                },
              }}
            >
              {(isExecuting || isSending) ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                executeMode === 'creator' ? 'Generate document' : 'Run capability'
              )}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Chat memory is cleared when you close this modal.
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Chat + composer once execution / conversation has started */}
      {!showIntroScreen && (
        <>
          {showAssistantLanding ? (
            <AssistantLanding
              ref={assistantLandingRef}
              executeMode={executeMode}
              onAddKnowledge={onAttachExistingContext}
              selectedModel={selectedModel}
              onModelChange={setSelectedModel}
              modelMenuAnchor={modelMenuAnchor}
              onModelMenuAnchorChange={setModelMenuAnchor}
              disabled={isChatBusy || isPreparingContext}
              refining={assistantLandingRefining}
              refineOptions={assistantRefineOptions || undefined}
              onSend={(text, sendOptions) => handleSendMessage(selectedModel || 'gpt-6-sol', text, sendOptions)}
              onBuild={(text) => {
                onAssistantLandingDismiss?.();
                if (String(text || '').trim()) {
                  const modelKey = selectedModel || 'gpt-6-sol';
                  const message = {
                    id: `build-user-${Date.now()}`,
                    type: 'user',
                    content: text,
                    timestamp: new Date(),
                  };
                  setTestChats((prev) => ({
                    ...prev,
                    [modelKey]: [...(prev[modelKey] || []), message],
                  }));
                }
                onAssistantBuildIntent?.(text);
              }}
              onRefine={(payload) => {
                onAssistantRefineIntent?.(payload);
              }}
            />
          ) : (
          <>
          <Box sx={threadOuterSx}>
          <Box 
            ref={chatContainerRef}
            onScroll={handleConversationScroll}
            sx={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              width: '100%',
              scrollbarGutter: isAssistantChatUi
                ? (isAssistantEmbeddedChat ? 'stable' : 'stable both-edges')
                : undefined,
              ...(isAssistantChatUi ? {} : { p: 2 }),
              bgcolor: isDragActive ? 'action.hover' : 'transparent',
              transition: 'background-color 0.15s ease',
            }}
          >
          <Box sx={scrollInnerSx}>
          <Stack spacing={isAssistantChatUi ? 2.5 : 2}>
            {showAssistantBusyEmptyState && (
              <Box
                sx={{
                  minHeight: 'min(280px, 38vh)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 3,
                }}
              >
                {isPreparingContext ? (
                  <Paper
                    elevation={2}
                    sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.25 }}
                  >
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      {contextPreparationLabel || `Adding ${PRODUCT_TERMS.temporaryKnowledge}...`}
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1.25} alignItems="center" sx={{ maxWidth: 460 }}>
                    <CircularProgress size={18} sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight={650} color="text.primary">
                      {builderStatusLabel || sendingStatusLabel || 'AI is working...'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, maxWidth: 360 }}>
                      Assistant is preparing the next step for this workspace.
                    </Typography>
                  </Stack>
                )}
              </Box>
            )}
            {currentMessages.length === 0 && !isPreparingContext && (!isAssistantChatUi || (!showAssistantLanding && !showAssistantBusyEmptyState)) && (
              <Box
                sx={{
                  minHeight: 'min(320px, 45vh)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  px: 3,
                }}
              >
                <Stack spacing={1} alignItems="center" sx={{ maxWidth: 520 }}>
                  <ChatModeIcon sx={{ fontSize: 26, color: 'text.disabled', mb: 0.25 }} />
                  <Typography variant="body2" fontWeight={500} color="text.secondary">
                    {emptyStateTitle ?? DEFAULT_CHAT_EMPTY_TITLE}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.55, maxWidth: 360 }}
                  >
                    {emptyStateDescription ?? DEFAULT_CHAT_EMPTY_DESCRIPTION}
                  </Typography>
                  {Array.isArray(starterSuggestions) && starterSuggestions.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'center', pt: 1 }}>
                      {starterSuggestions.map((suggestion) => {
                        const label = typeof suggestion === 'string' ? suggestion : suggestion.label;
                        const promptText = typeof suggestion === 'string' ? suggestion : suggestion.prompt;
                        return (
                          <Chip
                            key={label}
                            label={label}
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const text = String(promptText || label || '').trim();
                              if (text) handleSendMessage(selectedModel || 'gpt-6-sol', text);
                            }}
                            sx={{
                              maxWidth: 260,
                              borderRadius: 999,
                              fontSize: '0.75rem',
                              bgcolor: 'background.paper',
                              '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Stack>
              </Box>
            )}
            {currentMessages.map((message, index) => {
              // For the first user message (execute), show prompt only in chat if provided; API still gets full text
              const isFirstUserMessage = index === 0 && message.type === 'user';
              const displayContent = isFirstUserMessage && promptOnlyText != null && promptOnlyText !== ''
                ? promptOnlyText
                : (typeof message.content === 'string' ? message.content : String(message.content || ''));
              const builderFlowKind = message.builderFlow?.kind;
              const showBuilderMarkdown = !BUILDER_CARD_ONLY_KINDS.has(builderFlowKind);
              const messageContent = typeof message.content === 'string' ? message.content : String(message.content || '');
              return (
              <Box
                key={message.id}
                data-assistant-message={message.id}
                sx={{
                  ...(isAssistantChatUi && message.type === 'assistant' && message.id !== latestActionMessageId ? {
                    '& .MuiButton-contained': {
                      bgcolor: 'action.hover', color: 'text.secondary', boxShadow: 'none',
                      '&:hover': { bgcolor: 'action.selected', boxShadow: 'none' },
                    },
                  } : {}),
                  display: 'flex',
                  justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: message.type === 'assistant' && isAssistantChatUi ? 0 : (isAssistantChatUi ? 1.5 : 1.75),
                    maxWidth: message.type === 'user' ? USER_MESSAGE_MAX_WIDTH : isAssistantChatUi ? '100%' : ASSISTANT_MESSAGE_MAX_WIDTH,
                    ...(message.type === 'assistant' && isAssistantChatUi ? { width: '100%', minWidth: 0 } : {}),
                    bgcolor: message.type === 'user' ? activeUserSurface.bgcolor : activeAssistantSurface.bgcolor,
                    color: message.type === 'user' ? activeUserSurface.color : activeAssistantSurface.color,
                    border: message.type === 'user' && isAssistantChatUi
                      ? activeUserSurface.border
                      : (message.type === 'user' || isAssistantChatUi ? 'none' : '1px solid'),
                    borderColor: message.type === 'user'
                      ? (isAssistantChatUi ? 'rgba(0, 0, 0, 0.06)' : 'transparent')
                      : activeAssistantSurface.borderColor,
                    borderRadius: message.type === 'user' && isAssistantChatUi
                      ? activeUserSurface.borderRadius
                      : message.builderFlow || message.discovery ? '8px' : undefined,
                    boxShadow: message.type === 'user'
                      ? activeUserSurface.boxShadow
                      : activeAssistantSurface.boxShadow,
                  }}
                >
                  {message.type === 'assistant' ? (
                    <>
                      {message.isGenerating && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                          <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {message.loadingLabel || 'AI is working...'}
                          </Typography>
                        </Stack>
                      )}
                      {showBuilderMarkdown && messageContent.trim() && (
                        <SafeMarkdown
                          markdownBaseSx={assistantChatMarkdownSx}
                          sx={{
                            lineHeight: 1.62,
                            p: 0,
                            m: 0,
                            fontSize: '0.9375rem',
                            color: colors.textPrimary,
                            '& h1, & h2, & h3, & h4': {
                              mt: 1.25,
                              mb: 0.75,
                              fontWeight: 700,
                              letterSpacing: 0,
                              color: colors.textPrimary,
                            },
                            '& p': {
                              color: colors.textPrimary,
                            },
                            '& li': {
                              color: colors.textPrimary,
                              mb: 0.35,
                            },
                            '& strong': {
                              color: colors.textPrimary,
                              fontWeight: 700,
                            },
                            ...(isAssistantChatUi && builderFlowKind ? assistantDocumentSx : {}),
                          }}
                        >
                          {messageContent}
                        </SafeMarkdown>
                      )}
                      {message.discovery && Array.isArray(message.discovery.results) && (
                        <DiscoveryResultsList
                          kind={message.discovery.kind}
                          results={message.discovery.results}
                          onDiscoveryAction={onDiscoveryAction}
                          isBusy={isSending || isExecuting || isBuilderBusy}
                          workspacePromptIds={workspacePromptIds}
                          assistantContext={assistantContext}
                          builderCapabilityAdded={builderCapabilityAdded}
                          query={message.discovery.query || ''}
                        />
                      )}
                      {message.nextStepActions && Array.isArray(message.workflowActions?.actions) && (
                        <Box
                          sx={{
                            mt: 1.5,
                            px: 1.5,
                            py: 1.35,
                            border: '1px solid rgba(29, 78, 216, 0.16)',
                            borderRadius: '8px',
                            bgcolor: 'rgba(29, 78, 216, 0.028)',
                          }}
                        >
                          <Typography sx={{ color: colors.textPrimary, fontWeight: 750, fontSize: '0.875rem' }}>
                            {message.nextStepTitle || 'Capability added to your workspace'}
                          </Typography>
                          <Typography sx={{ mt: 0.35, color: colors.textSecondary, fontSize: '0.8125rem', lineHeight: 1.45 }}>
                            {message.nextStepDescription || 'Recommended next: add knowledge to make this capability more specific.'}
                          </Typography>
                          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mt: 1.15 }}>
                            {message.workflowActions.actions.map((action) => (
                              <Button
                                key={action.action}
                                size="small"
                                variant={action.variant || 'outlined'}
                                startIcon={getWorkflowActionIcon(action.action)}
                                onClick={() => handleWorkflowActionClick(action, message)}
                                sx={action.variant === 'contained'
                                  ? assistantPrimaryButtonSx
                                  : action.variant === 'text'
                                    ? assistantTextButtonSx
                                    : assistantSecondaryButtonSx}
                              >
                      {action.action === 'connect-mcp' ? 'Use in your AI tool' : action.label}
                              </Button>
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {!message.nextStepActions && ((message.workflowActions && Array.isArray(message.workflowActions.actions)) || Array.isArray(message.actions)) && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                          {(message.workflowActions?.actions || message.actions || []).map((action) => (
                            <Button
                              key={action.action}
                              size="small"
                              variant={action.variant || 'outlined'}
                              startIcon={getWorkflowActionIcon(action.action)}
                              onClick={() => handleWorkflowActionClick(action, message)}
                              sx={action.variant === 'contained'
                                ? assistantPrimaryButtonSx
                                : action.variant === 'text'
                                  ? assistantTextButtonSx
                                  : assistantSecondaryButtonSx}
                            >
                              {action.action === 'connect-mcp' ? 'Use in your AI tool' : action.label}
                            </Button>
                          ))}
                        </Stack>
                      )}
                      {message.builderFlow?.kind === 'summary' && (
                        <CreateFlowSummaryCard
                          buildGoal={message.builderFlow.payload?.buildGoal}
                          createFlowData={message.builderFlow.payload?.createFlowData}
                          confidence={message.builderFlow.payload?.confidence}
                          generating={message.builderFlow.payload?.generating}
                          generated={message.builderFlow.payload?.generated}
                          onConfirmGenerate={() => onBuilderAction?.({ action: 'confirm-generate', message })}
                          onFieldChange={(fieldKey, value) => onBuilderAction?.({
                            action: 'update-field',
                            payload: { fieldKey, value },
                            message,
                          })}
                        />
                      )}
                      {message.builderFlow?.kind === 'step' && (
                        <CreateFlowStepCard
                          question={message.builderFlow.payload?.question}
                          chips={message.builderFlow.payload?.chips}
                          selectedValue={message.builderFlow.payload?.selectedValue}
                          onChipSelect={(value) => onBuilderAction?.({ action: 'step-chip', payload: { value, stepIndex: message.builderFlow.payload?.stepIndex }, message })}
                        />
                      )}
                      {message.builderFlow?.kind === 'generated' && (
                        <GeneratedCapabilityCard
                          promptText={message.builderFlow.payload?.promptText}
                          appliedToShelf={Boolean(message.builderFlow.payload?.appliedToShelf)}
                          title={message.builderFlow.payload?.appliedToShelf ? 'Capability in builder' : 'Capability draft'}
                          applyLabel={message.builderFlow.payload?.applyMode === 'update' ? 'Apply update' : 'Continue Build'}
                          knowledgeStatus={message.builderFlow.payload?.knowledgeStatus}
                          knowledgeReview={message.builderFlow.payload?.knowledgeReview}
                          onTest={(text) => onBuilderAction?.({
                            action: 'test-generated',
                            payload: {
                              promptText: text,
                              capabilityType: message.builderFlow.payload?.capabilityType,
                              usageSurface: message.builderFlow.payload?.usageSurface,
                            },
                            message,
                          })}
                          onAttachKnowledge={() => onBuilderAction?.({ action: 'attach-existing-knowledge', message })}
                          onAddKnowledgeResult={(item, result) => onBuilderAction?.({ action: 'knowledge-add', payload: { item, result }, message })}
                          onOpenKnowledgeResult={(item, result) => onBuilderAction?.({ action: 'knowledge-open', payload: { item, result }, message })}
                          onApply={message.builderFlow.payload?.appliedToShelf
                            ? undefined
                            : (text) => onBuilderAction?.({ action: 'apply-generated', payload: { promptText: text }, message })}
                          onExpand={(text) => onBuilderAction?.({ action: 'expand-generated', payload: { promptText: text }, message })}
                        />
                      )}
                      {message.builderFlow?.kind === 'refine_review' && (
                        <Box sx={{ mt: 1.5 }}>
                        {message.builderFlow.payload?.sourceTitle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Refining: {message.builderFlow.payload.sourceTitle}</Typography>}
                        <Button variant="contained" size="small" sx={assistantPrimaryButtonSx} disabled={Boolean(message.builderFlow.payload?.disabled)} onClick={() => onBuilderAction?.({ action: 'generate-refined', payload: message.builderFlow.payload, message })}>
                          Generate revised capability
                        </Button>
                        </Box>
                      )}
                      {message.builderFlow?.kind === 'refined' && (
                        (() => {
                          const workspaceCapabilityId = message.builderFlow.payload?.workspaceCapabilityId;
                          const updatedCapability = Boolean(message.builderFlow.payload?.updatedCapability);
                          const isWorkspaceRefinement = assistantContext === 'standard' && Boolean(workspaceCapabilityId);
                          return (
                            <GeneratedCapabilityCard
                              promptText={message.builderFlow.payload?.promptText}
                              title={updatedCapability ? 'Capability updated in your workspace' : 'Refined capability'}
                              applyLabel={assistantContext === 'builder'
                                ? 'Apply to capability'
                                : isWorkspaceRefinement ? 'Save capability changes' : refineApplyLabel}
                              nextStepTitle={updatedCapability ? 'Keep improving this capability' : 'Ready to use this refinement?'}
                              nextStepDescription={updatedCapability
                                ? 'Use the updated capability now, add supporting knowledge, or refine it again.'
                                : isWorkspaceRefinement ? 'Saves this revision to the original capability and updates your workspace.'
                                  : 'Review the revision before continuing.'}
                              previewable
                              onFindKnowledge={updatedCapability
                                ? () => onBuilderAction?.({ action: 'find-workspace-knowledge', payload: { workspaceCapabilityId }, message })
                                : undefined}
                              onRun={updatedCapability
                                ? () => onBuilderAction?.({ action: 'run-workspace-capability', payload: { workspaceCapabilityId }, message })
                                : undefined}
                              onRefine={updatedCapability
                                ? () => onBuilderAction?.({ action: 'refine-workspace-capability', payload: { workspaceCapabilityId }, message })
                                : undefined}
                              onApply={updatedCapability
                                ? undefined
                                : (text) => onBuilderAction?.({
                                  action: assistantContext === 'builder'
                                    ? 'apply-refined'
                                    : isWorkspaceRefinement ? 'update-refined-capability' : 'create-from-refined',
                                  payload: { promptText: text, ...(workspaceCapabilityId ? { workspaceCapabilityId } : {}) },
                                  message,
                                })}
                            />
                          );
                        })()
                      )}
                      {message.builderFlow?.kind === 'knowledge_review' && (
                        <KnowledgeReviewCard
                          suggestion={message.builderFlow.payload?.suggestion}
                          recommendations={message.builderFlow.payload?.recommendations}
                          analyzingGaps={message.builderFlow.payload?.analyzingGaps}
                          searchingLibrary={message.builderFlow.payload?.searchingLibrary}
                          results={message.builderFlow.payload?.results}
                          discoveryError={message.builderFlow.payload?.discoveryError}
                          showReevaluate={Boolean(message.builderFlow.payload?.showReevaluate)}
                          onAddKnowledge={() => onBuilderAction?.({ action: 'add-knowledge', message })}
                          onAttachExisting={() => onBuilderAction?.({ action: 'attach-existing-knowledge', message })}
                          onReevaluate={() => onBuilderAction?.({ action: 'reevaluate-knowledge', message })}
                          onDone={() => onBuilderAction?.({ action: 'knowledge-done', message })}
                          doneLabel={assistantContext === 'builder'
                            ? 'Done'
                            : message.builderFlow.payload?.discoveryKnowledge ? 'Run capability' : 'Continue'}
                          nextStep={message.builderFlow.payload?.discoveryKnowledge
                            ? 'run'
                            : assistantContext === 'builder' ? 'done' : 'finish'}
                          onAddResult={(item, result) => onBuilderAction?.({ action: 'knowledge-add', payload: { item, result }, message })}
                          onOpenResult={(item, result) => onBuilderAction?.({ action: 'knowledge-open', payload: { item, result }, message })}
                        />
                      )}
                      {message.builderFlow?.kind === 'knowledge_discovery' && (
                        <KnowledgeDiscoveryCard
                          results={message.builderFlow.payload?.results}
                          error={message.builderFlow.payload?.error}
                          onAdd={(item, result) => onBuilderAction?.({ action: 'knowledge-add', payload: { item, result }, message })}
                          onOpen={(item, result) => onBuilderAction?.({ action: 'knowledge-open', payload: { item, result }, message })}
                        />
                      )}
                      {message.builderFlow?.kind === 'knowledge_suggest' && (
                        <KnowledgeSuggestionCard
                          suggestion={message.builderFlow.payload?.suggestion}
                          analyzing={message.builderFlow.payload?.analyzing}
                          showReevaluate={Boolean(message.builderFlow.payload?.showReevaluate)}
                          onAddKnowledge={() => onBuilderAction?.({ action: 'add-knowledge', message })}
                          onAttachExisting={() => onBuilderAction?.({ action: 'attach-existing-knowledge', message })}
                          onReevaluate={() => onBuilderAction?.({ action: 'reevaluate-knowledge', message })}
                          onDone={() => onBuilderAction?.({ action: 'knowledge-done', message })}
                          doneLabel={assistantContext === 'builder' ? 'Done' : 'Continue'}
                          nextStep={assistantContext === 'builder' ? 'done' : 'finish'}
                        />
                      )}
                      {message.builderFlow?.kind === 'metadata_invite' && (
                        <CreateFlowMetadataInviteCard
                          generating={message.builderFlow.payload?.generating}
                          onGenerate={() => onBuilderAction?.({ action: 'metadata-generate', message })}
                          onSkip={() => onBuilderAction?.({ action: 'metadata-skip', message })}
                        />
                      )}
                      {message.builderFlow?.kind === 'variables_invite' && (
                        <CreateFlowVariablesInviteCard
                          analyzing={message.builderFlow.payload?.analyzing}
                          onSuggest={() => onBuilderAction?.({ action: 'variables-suggest', message })}
                          onSkip={() => onBuilderAction?.({ action: 'variables-skip', message })}
                        />
                      )}
                      {message.builderFlow?.kind === 'variables_result' && (
                        <CreateFlowVariablesResultCard
                          summary={message.builderFlow.payload?.summary}
                          variableCount={message.builderFlow.payload?.variableCount}
                          onApply={() => onBuilderAction?.({
                            action: 'variables-apply',
                            payload: message.builderFlow.payload,
                            message,
                          })}
                          onSkip={() => onBuilderAction?.({ action: 'variables-keep', message })}
                        />
                      )}
                      {message.builderFlow?.kind === 'finalize_invite' && (
                        <CreateFlowFinalizeInviteCard
                          generating={message.builderFlow.payload?.generating}
                          onGenerate={() => onBuilderAction?.({ action: 'finalize-generate', message })}
                          onPreview={() => onBuilderAction?.({ action: 'preview-in-builder', message })}
                          onSkip={() => onBuilderAction?.({ action: 'finalize-skip', message })}
                        />
                      )}
                      {message.builderFlow?.kind === 'finalize_result' && (
                        <CreateFlowFinalizeResultCard
                          metadataApplied={message.builderFlow.payload?.metadataApplied}
                          metadataError={message.builderFlow.payload?.metadataError}
                          summary={message.builderFlow.payload?.summary}
                          variableCount={message.builderFlow.payload?.variableCount}
                          variablesError={message.builderFlow.payload?.variablesError}
                          onApply={() => onBuilderAction?.({
                            action: 'finalize-apply',
                            payload: message.builderFlow.payload,
                            message,
                          })}
                          onPreview={() => onBuilderAction?.({ action: 'preview-in-builder', message })}
                          onSkip={() => onBuilderAction?.({ action: 'finalize-keep', message })}
                        />
                      )}
                      {message.builderFlow?.kind === 'finalize_complete' && (
                        <CreateFlowFinalizeCompleteCard
                          status={message.builderFlow.payload?.status}
                          metadataApplied={message.builderFlow.payload?.metadataApplied}
                          variablesApplied={message.builderFlow.payload?.variablesApplied}
                          summary={message.builderFlow.payload?.summary}
                          error={message.builderFlow.payload?.error}
                          onRun={assistantContext === 'standard'
                            ? () => onBuilderAction?.({ action: 'run-generated-capability', message })
                            : undefined}
                          onView={assistantContext === 'standard'
                            ? () => onBuilderAction?.({ action: 'view-generated-capability', message })
                            : undefined}
                          onSave={assistantContext === 'standard'
                            ? () => onBuilderAction?.({ action: 'save-generated-capability', message })
                            : undefined}
                        />
                      )}
                      {message.builderFlow?.kind === 'builder_complete' && (
                        <Stack direction="row" sx={{ mt: 1.25 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => onBuilderAction?.({ action: 'complete-builder-session', message })}
                            sx={assistantPrimaryButtonSx}
                          >
                            Done
                          </Button>
                        </Stack>
                      )}
                    </>
                  ) : isAssistantChatUi ? (
                    <SafeMarkdown
                      markdownBaseSx={assistantChatMarkdownSx}
                      sx={{
                        lineHeight: activeUserSurface.lineHeight,
                        p: 0,
                        m: 0,
                        fontSize: '0.9375rem',
                        color: activeUserSurface.color,
                      }}
                    >
                      {displayContent}
                    </SafeMarkdown>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        color: USER_CHAT_SURFACE.color,
                        lineHeight: USER_CHAT_SURFACE.lineHeight,
                        fontSize: '0.9375rem',
                      }}
                    >
                      {displayContent}
                    </Typography>
                  )}
                  {showRunDiagnostics && message.contextDiagnostics && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                    >
                      {PRODUCT_TERMS.knowledgeUsed}: {message.contextDiagnostics}
                    </Typography>
                  )}
                  {showRunDiagnostics && message.creatorDiagnostics && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
                    >
                      Creator run: {message.creatorDiagnostics}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      color: message.type === 'user' ? activeUserSurface.timestampColor : 'text.secondary',
                      fontSize: '0.625rem',
                    }}
                  >
                    {message.timestamp?.toLocaleTimeString?.() || ''}
                  </Typography>
                </Paper>
                {message.type === 'user' && onSaveUserMessage && (
                  <Tooltip title="Save this message">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setUserMessageSaveTarget({ ...message, displayContent });
                        setUserMessageSaveAnchor(e.currentTarget);
                      }}
                      sx={{
                        mt: 0.5,
                        color: 'text.disabled',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'rgba(29, 78, 216, 0.06)'
                        }
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {message.type === 'assistant' && (
                  <Tooltip title={copySuccess[message.id] ? 'Copied!' : 'Copy response'}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(typeof message.content === 'string' ? message.content : String(message.content || ''));
                        setCopySuccess(prev => ({ ...prev, [message.id]: true }));
                        setTimeout(() => {
                          setCopySuccess(prev => {
                            const newState = { ...prev };
                            delete newState[message.id];
                            return newState;
                          });
                        }, 2000);
                      }}
                      sx={{
                        mt: 0.5,
                        color: 'text.disabled',
                        '&:hover': {
                          color: 'primary.main',
                          backgroundColor: 'rgba(29, 78, 216, 0.06)'
                        }
                      }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
            })}
            {/* Show spinner at bottom when waiting for AI (chat mode only; creator uses system strip below) */}
            {isChatBusy && currentMessages.length > 0 && !currentMessages.some((message) => message.isGenerating) && executeMode !== 'creator' && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: 1,
                  pl: 1,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: isAssistantChatUi ? 0 : 1.75,
                    maxWidth: ASSISTANT_MESSAGE_MAX_WIDTH,
                    bgcolor: activeAssistantSurface.bgcolor,
                    color: activeAssistantSurface.color,
                    border: isAssistantChatUi ? 'none' : '1px solid',
                    borderColor: activeAssistantSurface.borderColor,
                    boxShadow: activeAssistantSurface.boxShadow,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    {builderStatusLabel || sendingStatusLabel}
                  </Typography>
                </Paper>
              </Box>
            )}
            {/* Scroll anchor */}
            <div ref={chatEndRef} />
          </Stack>
          </Box>
          <Menu
            anchorEl={userMessageSaveAnchor}
            open={Boolean(userMessageSaveAnchor)}
            onClose={() => {
              setUserMessageSaveAnchor(null);
              setUserMessageSaveTarget(null);
            }}
          >
            <MenuItem
              onClick={() => {
                if (userMessageSaveTarget) {
                  onSaveUserMessage?.(userMessageSaveTarget, { includeContext: false });
                }
                setUserMessageSaveAnchor(null);
                setUserMessageSaveTarget(null);
              }}
            >
              Save capability
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (userMessageSaveTarget) {
                  onSaveUserMessage?.(userMessageSaveTarget, { includeContext: true });
                }
                setUserMessageSaveAnchor(null);
                setUserMessageSaveTarget(null);
              }}
            >
              Save capability with knowledge
            </MenuItem>
          </Menu>
          </Box>

          {/* Creator mode: system message below chat (AI thinking / Updates done) */}
          {executeMode === 'creator' && (
            <Box
              sx={{
                px: 2,
                py: 1,
                bgcolor: 'rgba(248, 250, 252, 0.85)',
                boxShadow: `0 -1px 0 ${EXECUTE_SURFACE_DIVIDER}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: 40,
              }}
            >
              {(isSending || isExecuting || isBuilderBusy) ? (
                <>
                  <CircularProgress size={14} sx={{ color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {builderStatusLabel || sendingStatusLabel}
                  </Typography>
                </>
              ) : creatorUpdatesDone ? (
                <Typography variant="caption" color="text.secondary">
                  Updates done — you can edit the document or suggest changes below.
                </Typography>
              ) : null}
            </Box>
          )}

          {/* Composer */}
          <Box
            sx={{
              ...(isAssistantChatUi
                ? { flexShrink: 0, width: '100%', pt: 1.75, pb: 2 }
                : {
                  p: 2,
                  pt: 1.75,
                  bgcolor: 'background.paper',
                  boxShadow: `0 -1px 0 ${EXECUTE_SURFACE_DIVIDER}`,
                }),
            }}
          >
            <Box sx={isAssistantChatUi ? assistantComposerColumnSx : conversationColumnSx}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: activeComposerSurface.borderColor,
                bgcolor: isDragActive
                  ? 'primary.50'
                  : activeComposerSurface.bgcolor,
                px: 2,
                pt: 1.5,
                pb: 1,
                minHeight: 56,
                transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
                boxShadow: activeComposerSurface.boxShadow,
                '&:focus-within': {
                  borderColor: activeComposerSurface.focusBorderColor || 'rgba(100, 116, 139, 0.55)',
                  bgcolor: activeComposerSurface.bgcolor,
                  boxShadow: activeComposerSurface.focusBoxShadow || activeComposerSurface.boxShadow,
                },
              }}
            >
              {isDragActive && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 6,
                    border: '1px dashed',
                    borderColor: CHAT_COMPOSER_SURFACE.borderColor,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    bgcolor: 'rgba(255, 255, 255, 0.72)',
                    zIndex: 2,
                  }}
                >
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    Drop files to add {PRODUCT_TERMS.temporaryKnowledge}
                  </Typography>
                </Box>
              )}
              <TextField
                inputRef={messageInputRef}
                fullWidth
                multiline
                minRows={1}
                maxRows={4}
                placeholder={
                  executeMode === 'creator' && creatorDocument?.trim()
                    ? 'Describe changes to the document'
                    : 'Type your message…'
                }
                variant="standard"
                disabled={isExecuting || isChatBusy || isPreparingContext || builderComposerLocked}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (isExecuting || isSending) {
                      handleStopExecution();
                    } else if (!isBuilderBusy) {
                      submitComposerMessage(currentModelKey);
                    }
                  }
                }}
                InputProps={{
                  disableUnderline: true,
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '0.9375rem',
                    alignItems: 'center',
                  },
                  '& textarea': {
                    resize: 'none',
                    pb: 5,
                    pt: 1,
                    minHeight: 48,
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                    color: 'text.primary',
                  },
                  '& textarea::placeholder': {
                    color: '#64748B',
                    opacity: 1,
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  left: 8,
                  right: 8,
                  bottom: 8,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Restart session">
                    <IconButton
                      size="small"
                      onClick={() => (onRestartSession || handleRestartSession)()}
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: CHAT_COMPOSER_SURFACE.borderColor,
                        color: '#64748B',
                        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={PRODUCT_TERMS.attachKnowledge}>
                    <IconButton
                      size="small"
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: CHAT_COMPOSER_SURFACE.borderColor,
                        color: '#64748B',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          color: 'text.primary',
                        },
                      }}
                      onClick={(e) => {
                        if (onAddTemporaryContext && onAttachExistingContext) {
                          setComposerContextMenuAnchor(e.currentTarget);
                        } else if (onAddTemporaryContext) {
                          onAddTemporaryContext();
                        }
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (isExecuting || isSending) {
                      handleStopExecution();
                    } else {
                      submitComposerMessage(currentModelKey);
                    }
                  }}
                  disabled={isPreparingContext || isBuilderBusy || builderComposerLocked || (isExecuting && !isSending)}
                  sx={{
                    width: 32,
                    height: 32,
                    boxShadow: 1,
                    bgcolor: (isExecuting || isSending) ? 'error.main' : 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: (isExecuting || isSending) ? 'error.dark' : 'primary.dark',
                    },
                  }}
                >
                  {isExecuting || isSending ? (
                    <StopIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <SendIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Box>
              {!!(onAddTemporaryContext && onAttachExistingContext) && (
                <ContextAddMenu
                  anchorEl={composerContextMenuAnchor}
                  open={Boolean(composerContextMenuAnchor)}
                  onClose={() => setComposerContextMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  onCreateTemporary={onAddTemporaryContext}
                  onAttachExisting={onAttachExistingContext}
                />
              )}
            </Box>
            </Box>
          </Box>
          </Box>
          </>
          )}
        </>
      )}
    </Box>
  );
});

PromptExecuteChatPanel.displayName = 'PromptExecuteChatPanel';

export { CHAT_AVAILABLE_MODELS };
export default PromptExecuteChatPanel;

